import React, { useState, useEffect } from 'react'
import { ViewMode, ToastMessage, Location, ThemeMode } from '../types'
import { LOCATIONS, fetchUserPublicResearch, updateUserProfile, getUserStats, getNotificationPreferences, updateNotificationPreferences, submitSupportRequest, NotificationPreferences } from '../lib/api'
import { fetchUserResearchWithFallback } from '../lib/apiWithFallback'
import { getProfileCache, setProfileCache, clearProfileCache, PROFILE_CACHE_TTL } from '../lib/profileCache'
import PageHeader from '../components/PageHeader'
import { useAuth } from '../contexts/AuthContext'
import { getResearchTitle } from '../lib/utils'
import { LoadingAnimation } from '../components/ui/LoadingAnimation'
import Avatar from '../components/ui/Avatar'
import { getBillingStatus, getBillingPortal, createStripeCustomer, subscribe, buySingleReport, confirmCheckout, getPrices, BillingStatus, PlanPrices, hasBillingIssueStatus } from '../lib/billingApi'
import { availableReportPacks, formatUsd, type ReportPackId } from '../lib/reportPacks'
import ReportPackPicker from '../components/billing/ReportPackPicker'
import { formatSubscriptionPeriodEndUnix } from '../lib/billingDisplay'
import CheckoutModal from '../components/billing/CheckoutModal'
import packageJson from '../../package.json'
import { analytics, useScreenEngagement } from '../lib/analytics'
import { isLogoutInProgress } from '../lib/authSession'
import { API_BASE_URL } from '../lib/apiBaseUrl'
import { filterProductionDiscoveryItems } from '../lib/reportDiscovery'
import { resolveAssetUrl, resolveResearchCardImageUrl } from '../lib/urlFormatting'
import { MOBILE_WELCOME_FEATURES, SparklesIcon, useAutoScrollingTicker } from '../components/onboarding/MobileWelcomeShared'
// Prefer package.json as the canonical source of truth for the app version.
// REACT_APP_VERSION is kept as an optional override (e.g. hotfix builds).
const APP_VERSION = packageJson.version || process.env.REACT_APP_VERSION || '0.6.0'

// Helper function to strip markdown from text
const stripMarkdown = (text: string): string => {
  if (!text) return ''
  
  return text
    // Remove headers
    .replace(/^#{1,6}\s+/gm, '')
    // Remove bold/italic
    .replace(/\*{1,3}([^*]+)\*{1,3}/g, '$1')
    .replace(/_{1,3}([^_]+)_{1,3}/g, '$1')
    // Remove links but keep text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    // Remove images
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '')
    // Remove code blocks
    .replace(/```[^`]*```/g, '')
    .replace(/`([^`]+)`/g, '$1')
    // Remove blockquotes
    .replace(/^>\s+/gm, '')
    // Remove horizontal rules
    .replace(/^---+$/gm, '')
    // Remove bullet points
    .replace(/^[*+-]\s+/gm, '')
    // Remove numbered lists
    .replace(/^\d+\.\s+/gm, '')
    // Clean up extra whitespace
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}



interface ProfilePageProps {
  viewMode: ViewMode
  onShowToast: (toast: Omit<ToastMessage, 'id'>) => void
  themeMode: ThemeMode
  onThemeChange: (theme: ThemeMode) => void
  userEmail?: string  // Optional: if provided, show this user's profile instead of current user
  onBack?: () => void  // Optional: callback to go back to previous page
  onResearchClick?: (research: any) => void  // Optional: callback when a research item is clicked
  onBillingStatusSync?: (status: BillingStatus | null) => void
  onNavigateToResearch?: () => void
}

type ProfileCompletionAction = {
  key: 'avatar' | 'username' | 'bio'
  label: string
  onClick: () => void
}

function MobileProfileCompletionCard({
  completionPercentage,
  actions,
}: {
  completionPercentage: number
  actions: ProfileCompletionAction[]
}) {
  return (
    <div
      data-testid="profile-completion-card"
      className="mb-8 rounded-[28px] bg-white p-5 shadow-sm ring-1 ring-gray-100 dark:bg-gray-900 dark:ring-gray-800"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="mb-3 h-2.5 rounded-full bg-gray-100 dark:bg-gray-800">
            <div
              className="h-full rounded-full bg-slate-950 dark:bg-white"
              style={{ width: `${completionPercentage}%` }}
            />
          </div>
          <h3 className="text-xl font-semibold text-slate-900 dark:text-white">
            Complete your profile
          </h3>
        </div>
        <span className="pt-0.5 text-base font-semibold text-slate-700 dark:text-slate-300">
          {completionPercentage}%
        </span>
      </div>

      <div className="mt-4 flex flex-wrap gap-3">
        {actions.map(action => (
          <button
            key={action.key}
            type="button"
            onClick={action.onClick}
            className="inline-flex items-center gap-2 rounded-full bg-slate-50 px-4 py-2.5 text-sm font-medium text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-700"
          >
            {action.key === 'avatar' ? (
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            ) : action.key === 'username' ? (
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16.5 6.75A3.75 3.75 0 1112.75 3a3.75 3.75 0 013.75 3.75zm-10.5 13.5a6.75 6.75 0 0113.5 0" />
              </svg>
            ) : (
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5h7m-7 4h7m-7 4h4m-9 6l2.5-.714a2 2 0 00.948-.55l9.344-9.345a2.121 2.121 0 10-3-3L6.448 14.736a2 2 0 00-.55.948L5 18.5z" />
              </svg>
            )}
            <span>{action.label}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

function MobileProfileWelcomeState({
  onStartResearch,
}: {
  onStartResearch?: () => void
}) {
  const {
    tickerRef,
    handleTickerScroll,
    pauseTicker,
    resumeTickerSoon,
  } = useAutoScrollingTicker()

  return (
    <div
      data-testid="profile-first-time-empty-state"
      className="overflow-hidden rounded-[32px] bg-gray-50 px-5 py-8 text-center shadow-sm ring-1 ring-gray-100 dark:bg-gray-900 dark:ring-gray-800"
    >
      <style>{`
        @keyframes profileWelcomeGraphicFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }

        @keyframes profileWelcomeOuterPulse {
          0%, 100% { transform: scale(1); opacity: 0.82; }
          50% { transform: scale(1.06); opacity: 0.96; }
        }

        @keyframes profileWelcomeInnerPulse {
          0%, 100% { transform: scale(1); opacity: 0.95; }
          50% { transform: scale(0.94); opacity: 0.72; }
        }

        @keyframes profileWelcomeSparkleTwinkle {
          0%, 100% { transform: scale(1) rotate(0deg); opacity: 1; }
          50% { transform: scale(1.08) rotate(4deg); opacity: 0.88; }
        }

        .profile-welcome-strip::-webkit-scrollbar {
          display: none;
        }
      `}</style>

      <div className="mx-auto flex max-w-md flex-col items-center text-center">
        <div
          className="relative mb-6 flex h-32 w-32 items-center justify-center sm:h-36 sm:w-36"
          style={{ animation: 'profileWelcomeGraphicFloat 4.8s ease-in-out infinite' }}
        >
          <div
            className="absolute inset-0 rounded-full bg-slate-100/80 dark:bg-slate-800/70"
            style={{ animation: 'profileWelcomeOuterPulse 6s ease-in-out infinite' }}
          />
          <div
            className="absolute inset-5 rounded-full bg-slate-50 dark:bg-slate-800/45"
            style={{ animation: 'profileWelcomeInnerPulse 5.2s ease-in-out infinite' }}
          />
          <div className="relative flex h-[76px] w-[76px] items-center justify-center rounded-[24px] bg-white text-slate-900 shadow-[0_18px_45px_rgba(15,23,42,0.10)] ring-1 ring-slate-100 dark:bg-slate-900 dark:text-white dark:ring-slate-800">
            <div style={{ animation: 'profileWelcomeSparkleTwinkle 3.2s ease-in-out infinite' }}>
              <SparklesIcon />
            </div>
          </div>
        </div>

        <h3 className="whitespace-nowrap text-[clamp(1.55rem,6.5vw,2.2rem)] font-semibold leading-[1.05] tracking-[-0.04em] text-slate-900 dark:text-white">
          Begin Your Research Journey
        </h3>
        <p className="mt-3 max-w-[18rem] text-[1.02rem] leading-7 text-slate-500 dark:text-slate-400">
          Discover insights powered by AI. Your first research is just a tap away.
        </p>

        <button
          type="button"
          onClick={() => onStartResearch?.()}
          disabled={!onStartResearch}
          className="mx-auto mt-6 flex min-h-[56px] w-full max-w-[24rem] items-center justify-center gap-2.5 rounded-[22px] bg-slate-950 px-5 text-[0.98rem] font-semibold text-white shadow-[0_14px_30px_rgba(15,23,42,0.18)] transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-600 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200 dark:disabled:bg-slate-700 dark:disabled:text-slate-300"
        >
          <svg className="h-[18px] w-[18px] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span className="whitespace-nowrap">Create Your First Research</span>
        </button>

        <div
          ref={tickerRef}
          data-testid="profile-welcome-feature-ticker"
          role="region"
          aria-label="Swipe to explore feature highlights"
          tabIndex={0}
          onScroll={handleTickerScroll}
          onPointerDown={pauseTicker}
          onPointerUp={resumeTickerSoon}
          onPointerCancel={resumeTickerSoon}
          onTouchStart={pauseTicker}
          onTouchEnd={resumeTickerSoon}
          onMouseEnter={pauseTicker}
          onMouseLeave={resumeTickerSoon}
          className="profile-welcome-strip mt-5 w-full overflow-x-auto touch-pan-x [scrollbar-width:none]"
          style={{ msOverflowStyle: 'none' }}
        >
          <div className="flex w-max items-center whitespace-nowrap text-[0.92rem] font-medium text-slate-500 dark:text-slate-400">
            {[0, 1].map(copyIndex => (
              <div
                key={copyIndex}
                className="flex shrink-0 items-center justify-center gap-3 px-4"
                aria-hidden={copyIndex === 1}
              >
                {MOBILE_WELCOME_FEATURES.map((feature, featureIndex) => (
                  <React.Fragment key={`${copyIndex}-${feature.key}`}>
                    <div className="inline-flex items-center gap-2">
                      {feature.icon}
                      <span>{feature.label}</span>
                    </div>
                    {featureIndex < MOBILE_WELCOME_FEATURES.length - 1 && (
                      <span className="h-1 w-1 rounded-full bg-slate-300 dark:bg-slate-600" aria-hidden="true" />
                    )}
                  </React.Fragment>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default function ProfilePage({ viewMode, onShowToast, themeMode, onThemeChange, userEmail, onBack, onResearchClick, onBillingStatusSync, onNavigateToResearch }: ProfilePageProps) {
  const { user, logout, checkAuthStatus } = useAuth()
  const currentUserId = user?.id ?? null
  
  // Screen engagement tracking
  const isOwnProfile = !userEmail || userEmail === user?.email
  const engagementTracking = useScreenEngagement({ screenName: 'profile', isOwnProfile })
  
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [location, setLocation] = useState('')
  const [locationSearch, setLocationSearch] = useState('')
  const [showLocationDropdown, setShowLocationDropdown] = useState(false)
  const [userResearch, setUserResearch] = useState<any[]>([])
  const [hasMore, setHasMore] = useState<boolean>(true)
  const [offset, setOffset] = useState<number>(0)
  const PAGE_SIZE = 10  // Reduced from 20 to 10 for faster initial load
  const [isLoadingResearch, setIsLoadingResearch] = useState(false)
  const [profileUser, setProfileUser] = useState<any>(null)
  const [stats, setStats] = useState<{ total: number; public: number; private: number }>({ total: 0, public: 0, private: 0 })
  const [statsLoaded, setStatsLoaded] = useState(false)
  const [visibleCount, setVisibleCount] = useState<number>(PAGE_SIZE)
  const loadMoreRef = React.useRef<HTMLDivElement | null>(null)
  const lastItemRef = React.useRef<HTMLDivElement | null>(null)
  const anchorRef = React.useRef<{ elId: string | null; top: number } | null>(null)
  const previousUserIdRef = React.useRef<number | null>(null)
  const hydratedFromCacheRef = React.useRef(false)
  const avatarFileInputRef = React.useRef<HTMLInputElement | null>(null)
  const trackedProfileResearchIdsRef = React.useRef<Set<string>>(new Set())
  const trackedProfileViewRef = React.useRef<string | null>(null)

  const captureAnchor = () => {
    // Find the first card that is at or below the top of viewport
    const cards = Array.from(document.querySelectorAll('[data-profile-card="true"]')) as HTMLElement[]
    let chosen: HTMLElement | null = null
    for (const card of cards) {
      const rect = card.getBoundingClientRect()
      if (rect.top >= 0) { chosen = card; break }
    }
    if (!chosen && cards.length > 0) {
      chosen = cards[cards.length - 1]
    }
    anchorRef.current = { elId: chosen?.id || null, top: chosen ? chosen.getBoundingClientRect().top : 0 }
  }

  const restoreAnchor = () => {
    const anchor = anchorRef.current
    if (!anchor) return
    const el = anchor.elId ? document.getElementById(anchor.elId) : null
    const newTop = el ? el.getBoundingClientRect().top : 0
    const delta = newTop - anchor.top
    if (Math.abs(delta) > 0) {
      window.scrollBy({ top: delta })
    }
  }
  const [activeSection, setActiveSection] = useState<'timeline' | 'settings' | 'billing'>('timeline')
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false)
  const [avatarPrimaryColor, setAvatarPrimaryColor] = useState<string>('#6366f1') // Default indigo
  const [username, setUsername] = useState('')
  const [bio, setBio] = useState('')
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null)
  const [checkingUsername, setCheckingUsername] = useState(false)
  const [usernameError, setUsernameError] = useState<string | null>(null)
  const [isEditMode, setIsEditMode] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [originalData, setOriginalData] = useState({
    firstName: '',
    lastName: '',
    location: '',
    username: '',
    bio: ''
  })
  
  // Notification Preferences State
  const [notificationPrefs, setNotificationPrefs] = useState<NotificationPreferences>({
    research_complete: true,
    research_failed: true,
    marketing: false
  })
  const [notificationPrefsLoading, setNotificationPrefsLoading] = useState(true)
  const [notificationPrefsSaving, setNotificationPrefsSaving] = useState(false)
  
  // Settings Sub-Section State
  const [settingsSubSection, setSettingsSubSection] = useState<'main' | 'profile' | 'notifications' | 'privacy' | 'support' | 'account'>('main')
  
  // Support Form State
  const [supportSubject, setSupportSubject] = useState('')
  const [supportMessage, setSupportMessage] = useState('')
  const [supportSubmitting, setSupportSubmitting] = useState(false)
  const [supportSubmitted, setSupportSubmitted] = useState(false)
  
  // Check if viewing another user's profile
  const isViewingOtherUser = Boolean(userEmail && userEmail !== user?.email)

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Load notification preferences when settings section is active
  useEffect(() => {
    if (user && !isViewingOtherUser && activeSection === 'settings' && !isLogoutInProgress()) {
      loadNotificationPreferences()
    }
  }, [activeSection, isViewingOtherUser, user?.id])
  
  const loadNotificationPreferences = async () => {
    if (!user || isViewingOtherUser || isLogoutInProgress()) {
      return
    }
    try {
      setNotificationPrefsLoading(true)
      const prefs = await getNotificationPreferences()
      if (isLogoutInProgress()) {
        return
      }
      setNotificationPrefs(prefs)
    } catch (error) {
      if (!isLogoutInProgress()) {
        console.error('Failed to load notification preferences:', error)
      }
    } finally {
      setNotificationPrefsLoading(false)
    }
  }
  
  const handleNotificationPrefChange = async (key: keyof NotificationPreferences, value: boolean) => {
    try {
      setNotificationPrefsSaving(true)
      const updatedPrefs = await updateNotificationPreferences({ [key]: value })
      setNotificationPrefs(updatedPrefs)
      onShowToast({
        type: 'success',
        title: 'Preferences updated',
        description: 'Your notification preferences have been saved'
      })
    } catch (error) {
      console.error('Failed to update notification preference:', error)
      onShowToast({
        type: 'error',
        title: 'Update failed',
        description: 'Failed to update your preferences'
      })
    } finally {
      setNotificationPrefsSaving(false)
    }
  }
  
  const handleSupportSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!supportMessage.trim()) {
      onShowToast({
        type: 'error',
        title: 'Message required',
        description: 'Please enter a message'
      })
      return
    }
    
    try {
      setSupportSubmitting(true)
      await submitSupportRequest(supportSubject || 'Support Request', supportMessage)
      setSupportSubmitted(true)
      setSupportSubject('')
      setSupportMessage('')
      onShowToast({
        type: 'success',
        title: 'Request submitted',
        description: 'We will respond to your inquiry as soon as possible'
      })
    } catch (error) {
      console.error('Failed to submit support request:', error)
      onShowToast({
        type: 'error',
        title: 'Submission failed',
        description: error instanceof Error ? error.message : 'Failed to submit your request'
      })
    } finally {
      setSupportSubmitting(false)
    }
  }

  const loadStats = React.useCallback(async () => {
    if (!currentUserId || isViewingOtherUser || isLogoutInProgress()) return
    try {
      const s = await getUserStats()
      if (isLogoutInProgress()) {
        return
      }
      setStats({ total: s.total, public: s.public, private: s.private })
      setStatsLoaded(true)
    } catch (e) {
      setStatsLoaded(false)
    }
  }, [currentUserId, isViewingOtherUser])

  // Load user's public research if viewing another user
  useEffect(() => {
    console.log('🔵 ProfilePage: User changed, resetting ALL state')
    console.log('🔵 userEmail:', userEmail, 'isViewingOtherUser:', isViewingOtherUser, 'user.id:', currentUserId, 'user.email:', user?.email)

    if (isViewingOtherUser && userEmail) {
      console.log('🔵 Loading OTHER user research for:', userEmail)
      hydratedFromCacheRef.current = false
      setUserResearch([])
      setOffset(0)
      setHasMore(true)
      setProfileUser(null)
      loadUserPublicResearch()
      return
    }

    if (!user || !currentUserId) {
      hydratedFromCacheRef.current = false
      setUserResearch([])
      setOffset(0)
      setHasMore(true)
      setProfileUser(null)
      return
    }

    if (previousUserIdRef.current && currentUserId && previousUserIdRef.current !== currentUserId) {
      clearProfileCache(previousUserIdRef.current)
    }
    if (currentUserId) {
      previousUserIdRef.current = currentUserId
    }
    setProfileUser(null)

    const cached = getProfileCache(currentUserId)
    if (cached) {
      console.log('🔵 Hydrated profile from cache for user:', user.email)
      hydratedFromCacheRef.current = true
      setUserResearch(cached.research)
      setHasMore(cached.hasMore)
      setOffset(cached.offset)
      setVisibleCount(cached.visibleCount || Math.min(PAGE_SIZE, cached.research.length))
      setStats(cached.stats)
      setStatsLoaded(cached.statsLoaded)
      setIsLoadingResearch(false)

      const cacheAge = Date.now() - cached.timestamp
      const shouldRefreshNow = cacheAge > PROFILE_CACHE_TTL / 2
      const refresh = () => loadCurrentUserResearch(true, { silent: true })
      if (shouldRefreshNow) {
        refresh()
      } else {
        setTimeout(refresh, 500)
      }
      if (!cached.statsLoaded || shouldRefreshNow) {
        loadStats()
      }
      return
    }

    hydratedFromCacheRef.current = false
    setUserResearch([])
    setOffset(0)
    setHasMore(true)
      console.log('🔵 No cache found, loading fresh research for:', user.email)
    loadCurrentUserResearch(true)
    loadStats()
  }, [userEmail, isViewingOtherUser, currentUserId, loadStats])  // Use user.id instead of user object to prevent unnecessary re-renders

  // (Article overlay for Profile is managed in App; no local popstate handling here)

  // Background refresh every 5 minutes
  useEffect(() => {
    if (!isViewingOtherUser && !user) {
      return
    }
    const interval = setInterval(() => {
      if (isLogoutInProgress()) {
        return
      }
      if (isViewingOtherUser && userEmail) {
        loadUserPublicResearch()
      } else if (!isViewingOtherUser && user) {
        loadCurrentUserResearch(true, { silent: true })  // Force refresh on background reload without UI flicker
      }
    }, 300000)
    return () => clearInterval(interval)
  }, [isViewingOtherUser, userEmail, currentUserId])

  // Initialize form fields with user data
  useEffect(() => {
    if (user && !isViewingOtherUser) {
      // Split full name into first and last name
      const nameParts = user.name?.split(' ') || []
      const initialFirstName = nameParts[0] || ''
      const initialLastName = nameParts.slice(1).join(' ') || ''
      
      setFirstName(initialFirstName)
      setLastName(initialLastName)
      
      // Load saved profile data from localStorage if available
      const savedProfile = localStorage.getItem('userProfile')
      if (savedProfile) {
        try {
          const profile = JSON.parse(savedProfile)
          setFirstName(profile.firstName || initialFirstName)
          setLastName(profile.lastName || initialLastName)
          setLocation(profile.location || '')
          setLocationSearch(profile.location || '')
          setUsername(profile.username || '')
          setBio(profile.bio || '')
          
          // Set original data for cancel functionality
          setOriginalData({
            firstName: profile.firstName || initialFirstName,
            lastName: profile.lastName || initialLastName,
            location: profile.location || '',
            username: profile.username || '',
            bio: profile.bio || ''
          })
        } catch (e) {
          console.error('Failed to load saved profile:', e)
        }
      }
      
      // Also try to load username and bio from backend
      const loadUserProfile = async () => {
        try {
          const { getUserProfile } = await import('../lib/api')
          const profile = await getUserProfile()
          if (profile.user) {
            setUsername(profile.user.username || '')
            setBio(profile.user.bio || '')
            setOriginalData(prev => ({
              ...prev,
              username: profile.user.username || '',
              bio: profile.user.bio || ''
            }))
          }
        } catch (e) {
          console.error('Failed to load user profile from backend:', e)
        }
      }
      loadUserProfile()
    }
  }, [user, isViewingOtherUser])

  // Close location dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (!target.closest('.location-dropdown-container')) {
        setShowLocationDropdown(false)
      }
    }

    if (showLocationDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showLocationDropdown])

  const loadUserPublicResearch = async () => {
    try {
      setIsLoadingResearch(true)
      if (!userEmail) return
      
      const data = await fetchUserPublicResearch(userEmail)
      setProfileUser(data.user)
      
      // Format the research data to match the expected structure
      const formattedResearch = filterProductionDiscoveryItems(data.research).map((research: any) => ({
        id: research.id,
        title: research.title || research.query,
        query: research.query,
        summary: research.results?.summary || research.summary || '',
        created_at: research.created_at,
        completed_at: research.completed_at,
        status: research.status,
        author_name: research.author_name,
        author_avatar: research.author_avatar,
        image_url: resolveResearchCardImageUrl(research.image_url, API_BASE_URL),
        results: research.results || {
          report_text: research.report_text || '',
          summary: research.summary || '',
          key_findings: research.key_findings || [],
          sources: research.sources || []
        },
        model: research.model,
        is_public: research.is_public
      }))
      
      setUserResearch(formattedResearch)
      setVisibleCount(Math.min(PAGE_SIZE, formattedResearch.length))
    } catch (error) {
      console.error('Failed to load user research:', error)
      onShowToast({
        type: 'error',
        title: 'Failed to load user profile',
        description: error instanceof Error ? error.message : 'Could not load user research'
      })
    } finally {
      setIsLoadingResearch(false)
    }
  }

  const loadCurrentUserResearch = async (forceRefresh: boolean = false, options?: { silent?: boolean }) => {
    const shouldShowLoading = !options?.silent
    if (!user || isViewingOtherUser || isLogoutInProgress()) {
      if (shouldShowLoading) {
        setIsLoadingResearch(false)
      }
      return
    }
    try {
      if (shouldShowLoading) {
        setIsLoadingResearch(true)
      }
      // Use cache by default, bypass cache if forceRefresh is true
      const cacheBuster = forceRefresh ? Date.now().toString() : ''
      // Fetch a smaller batch initially for faster load; rely on onClick lazy fetch for details
      const effectiveOffset = forceRefresh ? 0 : offset
      
      // Use optimized endpoint to avoid loading full report_text
      const response = await fetchUserResearchWithFallback(PAGE_SIZE, effectiveOffset, cacheBuster)
      if (isLogoutInProgress()) {
        return
      }
      const research = filterProductionDiscoveryItems(response.results)
      
      // Format and filter completed research
      // NOTE: We're only loading summaries here for performance (no report_text)
      // Full content will be lazy-loaded when user clicks on an item
      const formattedResearch = research
        .filter((r: any) => r.status === 'completed')
        .map((r: any) => ({
          id: r.id,
          title: r.title || r.query,
          query: r.query,
          category: r.category,
          summary: r.results?.summary || r.summary || '',
          created_at: r.created_at,
          completed_at: r.completed_at,
          status: r.status,
          is_public: r.is_public,
          image_url: resolveResearchCardImageUrl(r.image_url, API_BASE_URL),
          is_intelligence_report: r.is_intelligence_report,
          intelligence_report_id: r.intelligence_report_id,
          research_id: r.research_id,
          depth: r.depth,
          results: r.results ? {
            ...r.results,
            report_text: ''
          } : {
            report_text: '',
            summary: r.summary || '',
            key_findings: r.key_findings || [],
            sources: r.sources || []
          },
          model: r.model,
          author_name: r.author_name || user?.name,
          author_avatar: r.author_avatar || user?.avatar_url,
          needs_full_fetch: true
        }))
      
      if (forceRefresh || effectiveOffset === 0) {
        setUserResearch(formattedResearch)
        setVisibleCount(Math.min(PAGE_SIZE, formattedResearch.length))
        setOffset(PAGE_SIZE)  // Start offset for next page
      } else {
        // Append new items without reload
        setUserResearch(prev => {
          console.log('Appending research items:', {
            previous: prev.length,
            new: formattedResearch.length,
            total: prev.length + formattedResearch.length
          })
          return [...prev, ...formattedResearch]
        })
        setOffset(effectiveOffset + PAGE_SIZE)
      }
      
      // Check if more items available
      setHasMore(formattedResearch.length === PAGE_SIZE)
      console.log('Load more state:', {
        loaded: formattedResearch.length,
        hasMore: formattedResearch.length === PAGE_SIZE,
        nextOffset: effectiveOffset + PAGE_SIZE
      })
    } catch (error) {
      if (!isLogoutInProgress()) {
        console.error('Failed to load research:', error)
      }
    } finally {
      if (shouldShowLoading) {
        setIsLoadingResearch(false)
      }
    }
  }

  // Removed automatic infinite scroll - now using "Load More" button for better UX

    const handleSaveSettings = async () => {
    try {
      setIsSaving(true)
      
      // Check username availability one more time if changed
      if (username && usernameError) {
        onShowToast({
          type: 'error',
          title: 'Invalid username',
          description: usernameError
        })
        setIsSaving(false)
        return
      }
      
      // Call API to update user profile
      const result = await updateUserProfile(firstName, lastName, location)
      
      // Update username and bio if changed
      const { updateUserIdentity } = await import('../lib/api')
      await updateUserIdentity(username || null, bio || null)
      
      // Save to localStorage as well for persistence
      const newProfileData = {
        firstName,
        lastName,
        location,
        username,
        bio
      }
      localStorage.setItem('userProfile', JSON.stringify(newProfileData))
      
      // Update original data
      setOriginalData(newProfileData)
      
      // Exit edit mode
      setIsEditMode(false)
      
      // Update auth context with new name if needed
      if (result.user && user) {
        // The auth context should be updated with the new name
        // This might require refreshing the user data or updating the auth context
        await checkAuthStatus()
      }

      onShowToast({
        type: 'success',
        title: 'Profile updated',
        description: 'Your profile information has been saved'
      })
    } catch (error) {
      console.error('Profile update error:', error)
      onShowToast({
        type: 'error',
        title: 'Update failed',
        description: error instanceof Error ? error.message : 'Failed to update your profile'
      })
    } finally {
      setIsSaving(false)
    }
  }
  
  const handleCancelEdit = () => {
    // Restore original values
    setFirstName(originalData.firstName)
    setLastName(originalData.lastName)
    setLocation(originalData.location)
    setLocationSearch(originalData.location)
    setUsername(originalData.username)
    setBio(originalData.bio)
    setUsernameError(null)
    setUsernameAvailable(null)
    setIsEditMode(false)
  }

  const handleLogout = async () => {
    try {
      await logout()
    onShowToast({
      type: 'info',
      title: 'Logged out',
      description: 'You have been logged out successfully'
    })
      // The logout function in AuthContext will clear the user state
      // which will trigger the redirect to login page via App.tsx
    } catch (error) {
      onShowToast({
        type: 'error',
        title: 'Logout Failed',
        description: 'Failed to log out. Please try again.'
      })
    }
  }

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      onShowToast({
        type: 'error',
        title: 'File too large',
        description: 'Please select an image smaller than 10MB'
      })
      return
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      onShowToast({
        type: 'error',
        title: 'Invalid file type',
        description: 'Please select an image file'
      })
      return
    }

    const formData = new FormData()
    formData.append('file', file)
    
    try {
      setIsUploadingAvatar(true)
      const token = localStorage.getItem('access_token')
      
      if (!token) {
        throw new Error('No authentication token found. Please log in again.')
      }
      
      const response = await fetch(`${API_BASE_URL}/auth/profile/avatar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to upload avatar' }))
        throw new Error(errorData.detail || `Failed to upload avatar (${response.status})`)
      }

      const data = await response.json()
      
      console.log('🔵 Avatar upload response:', data)
      console.log('🔵 Avatar URL length:', data.avatar_url?.length)
      console.log('🔵 Saved length from backend:', data.saved_length)
      
      if (data.saved_length === 0) {
        console.error('🔴 CRITICAL: Backend reports avatar was NOT saved to database!')
        onShowToast({
          type: 'error',
          title: 'Upload Failed',
          description: 'Avatar upload failed - not saved to database. Check backend logs.'
        })
        return
      }
      
      if (data.saved_length && data.saved_length < data.avatar_url?.length * 0.9) {
        console.error('🔴 WARNING: Avatar was truncated in database!', {
          original: data.avatar_url?.length,
          saved: data.saved_length
        })
        onShowToast({
          type: 'error',
          title: 'Upload Failed',
          description: 'Avatar was truncated in database. Try a smaller image.'
        })
        return
      }
      
      console.log('✅ Avatar appears to be saved correctly, refreshing auth...')
      
      // Update the user context to reflect the new avatar once.
      if (!isLogoutInProgress()) {
        await checkAuthStatus()
        console.log('🔵 Avatar refresh completed, user has avatar:', !!user?.avatar_url)
      }
      
      console.log('🔵 Final user state:', { 
        email: user?.email, 
        hasAvatar: !!user?.avatar_url,
        avatarLength: user?.avatar_url?.length 
      })
      
      // No page reload - let React handle the update
      // The checkAuthStatus() calls above should update the user context
      
      onShowToast({
        type: 'success',
        title: 'Avatar uploaded',
        description: 'Your profile picture has been updated successfully!'
      })
    } catch (error) {
      console.error('Avatar upload failed:', error)
      onShowToast({
        type: 'error',
        title: 'Upload failed',
        description: error instanceof Error ? error.message : 'Failed to upload avatar. Please try again.'
      })
    } finally {
      setIsUploadingAvatar(false)
    }
  }

  const displayUser = isViewingOtherUser ? profileUser : user
  const displayEmail = displayUser?.email || userEmail || user?.email
  const emailLocalPart = (displayEmail || '').split('@')[0] || ''
  // Use correct name logic: for other users prefer their profile data; for self prefer first/last typed values, then user.name
  const displayName = isViewingOtherUser 
    ? (displayUser?.name || emailLocalPart) 
    : ((firstName || lastName) ? `${firstName} ${lastName}`.trim() : (displayUser?.name || emailLocalPart))
  
  // Handle avatar URL properly
  const getAvatarUrl = (avatarUrl: string | null | undefined, email: string | undefined) => {
    if (!avatarUrl) {
      return `https://api.dicebear.com/7.x/avataaars/svg?seed=${email || 'default'}`
    }
    
    // If it's a data URL (base64), use it directly
    if (avatarUrl.startsWith('data:')) {
      return avatarUrl
    }
    
    // If it's a full URL (http/https), use it directly
    if (avatarUrl.startsWith('http://') || avatarUrl.startsWith('https://')) {
      return avatarUrl
    }
    
    // Otherwise, it's a relative path, prepend API_BASE_URL
    return `${API_BASE_URL}${avatarUrl}`
  }
  
  const displayAvatar = getAvatarUrl(displayUser?.avatar_url, displayEmail)
  const hasPersistedAvatar = Boolean(user?.avatar_url)
  const hasUsername = Boolean(username.trim())
  const hasBio = Boolean(bio.trim())

  const openProfileEditor = React.useCallback(() => {
    setActiveSection('settings')
    setSettingsSubSection('profile')
    setIsEditMode(true)
    engagementTracking.trackSettingsOpened()
  }, [engagementTracking])

  const openAvatarPicker = React.useCallback(() => {
    avatarFileInputRef.current?.click()
  }, [])

  const profileCompletionActions = React.useMemo<ProfileCompletionAction[]>(() => {
    if (isViewingOtherUser) return []

    const actions: ProfileCompletionAction[] = []

    if (!hasPersistedAvatar) {
      actions.push({ key: 'avatar', label: 'Add photo', onClick: openAvatarPicker })
    }
    if (!hasUsername) {
      actions.push({ key: 'username', label: 'Claim username', onClick: openProfileEditor })
    }
    if (!hasBio) {
      actions.push({ key: 'bio', label: 'Write bio', onClick: openProfileEditor })
    }

    return actions
  }, [hasBio, hasPersistedAvatar, hasUsername, isViewingOtherUser, openAvatarPicker, openProfileEditor])

  const profileCompletionPercentage = Math.round(
    ((hasPersistedAvatar ? 1 : 0) + (hasUsername ? 1 : 0) + (hasBio ? 1 : 0)) / 3 * 100
  )

  // Convert hex to rgba
  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }

  // Extract primary color from avatar image
  const extractPrimaryColor = (imageUrl: string) => {
    const img = new Image()
    
    // Only set crossOrigin for non-data URLs
    if (!imageUrl.startsWith('data:')) {
      img.crossOrigin = 'anonymous'
    }
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        if (!ctx) return

        // Scale down for performance
        const size = 50
        canvas.width = size
        canvas.height = size
        ctx.drawImage(img, 0, 0, size, size)

        const imageData = ctx.getImageData(0, 0, size, size)
        const pixels = imageData.data

        // Simple color extraction - find dominant color
        let r = 0, g = 0, b = 0, count = 0

        for (let i = 0; i < pixels.length; i += 4) {
          // Skip very light or very dark pixels
          const brightness = (pixels[i] + pixels[i + 1] + pixels[i + 2]) / 3
          if (brightness > 30 && brightness < 220) {
            r += pixels[i]
            g += pixels[i + 1]
            b += pixels[i + 2]
            count++
          }
        }

        if (count > 0) {
          r = Math.floor(r / count)
          g = Math.floor(g / count)
          b = Math.floor(b / count)
          
          // Boost saturation for better visual effect
          const max = Math.max(r, g, b)
          const min = Math.min(r, g, b)
          const delta = max - min
          
          if (delta > 10) {
            const boost = 1.5  // Increased boost for more vibrancy
            r = Math.min(255, Math.floor(min + (r - min) * boost))
            g = Math.min(255, Math.floor(min + (g - min) * boost))
            b = Math.min(255, Math.floor(min + (b - min) * boost))
          }
          
          // Ensure the color is bright enough
          const brightness = (r + g + b) / 3
          if (brightness < 100) {
            // Lighten dark colors
            const lightenFactor = 100 / brightness
            r = Math.min(255, Math.floor(r * lightenFactor))
            g = Math.min(255, Math.floor(g * lightenFactor))
            b = Math.min(255, Math.floor(b * lightenFactor))
          }
          
          // Convert to hex
          const hex = '#' + [r, g, b].map(x => {
            const hex = x.toString(16)
            return hex.length === 1 ? '0' + hex : hex
          }).join('')
          
          setAvatarPrimaryColor(hex)
        }
      } catch (error) {
        // Fallback to a nice default color on error
        setAvatarPrimaryColor('#6366f1')
      }
    }

    img.onerror = () => {
      // Failed to load avatar, use default color
      setAvatarPrimaryColor('#6366f1')
    }

    img.src = imageUrl
  }

  // Extract color when avatar changes
  useEffect(() => {
    if (displayAvatar) {
      // Extract color for all avatars, including dicebear
      extractPrimaryColor(displayAvatar)
    }
  }, [displayAvatar])

  useEffect(() => {
    if (isViewingOtherUser || !currentUserId) return
    if (userResearch.length === 0) {
      return
    }
    setProfileCache(currentUserId, {
      research: userResearch,
      offset,
      hasMore,
      stats,
      statsLoaded,
      visibleCount
    })
  }, [isViewingOtherUser, currentUserId, userResearch, offset, hasMore, stats, statsLoaded, visibleCount])

  // Stats: use backend totals for own profile once loaded; otherwise fallback to visible items
  const totalResearch = isViewingOtherUser 
    ? userResearch.length 
    : (statsLoaded ? stats.total : userResearch.length)
  const publicResearch = isViewingOtherUser 
    ? userResearch.length 
    : (statsLoaded ? stats.public : userResearch.filter(r => r.is_public).length)
  const privateResearch = isViewingOtherUser 
    ? 0 
    : (statsLoaded ? stats.private : Math.max(0, totalResearch - publicResearch))
  const showOwnMobileProfileOnboarding =
    viewMode === 'mobile' &&
    !isViewingOtherUser &&
    activeSection === 'timeline' &&
    !isLoadingResearch &&
    statsLoaded &&
    totalResearch === 0
  const showOwnMobileProfileCompletionCard =
    showOwnMobileProfileOnboarding &&
    profileCompletionActions.length > 0
  const profileAnalyticsId =
    displayUser?.id?.toString() || (isViewingOtherUser ? displayEmail || null : user?.id?.toString() || null)

  useEffect(() => {
    if (!profileAnalyticsId) return
    if (trackedProfileViewRef.current === profileAnalyticsId) return

    analytics.trackPageViewed({
      page: 'profile',
      referrer: document.referrer,
      view_mode: viewMode,
      is_own_profile: isOwnProfile,
      profile_user_id: profileAnalyticsId,
    })
    analytics.trackProfileViewed({
      profile_user_id: profileAnalyticsId,
      is_own_profile: isOwnProfile,
      research_count: totalResearch,
    })
    trackedProfileViewRef.current = profileAnalyticsId
  }, [isOwnProfile, profileAnalyticsId, totalResearch, viewMode])

  useEffect(() => {
    if (!showOwnMobileProfileOnboarding) {
      return
    }
  }, [activeSection, currentUserId, isLoadingResearch, privateResearch, profileCompletionActions.length, publicResearch, showOwnMobileProfileOnboarding, statsLoaded, totalResearch, userResearch.length])

  // Visible slice for smooth pagination (like Research page)
  const visibleResearch = userResearch.slice(0, visibleCount)

  useEffect(() => {
    visibleResearch.forEach((research) => {
      const trackingId =
        research.intelligence_report_id ||
        research.id?.toString() ||
        research.research_id?.toString()
      if (!trackingId || trackedProfileResearchIdsRef.current.has(trackingId)) return

      trackedProfileResearchIdsRef.current.add(trackingId)
      engagementTracking.trackCardViewed()
    })
  }, [engagementTracking, visibleResearch])

  // ----------------------------
  // Billing state and handlers
  // ----------------------------
  const [billingLoaded, setBillingLoaded] = useState(false)
  const [billingError, setBillingError] = useState<string | null>(null)
  const [purchaseLoading, setPurchaseLoading] = useState<string | null>(null)
  const [purchaseError, setPurchaseError] = useState<string | null>(null)
  const [reportPack, setReportPack] = useState<ReportPackId>('single')
  const [checkoutSecret, setCheckoutSecret] = useState<string | null>(null)
  const [checkoutSessionId, setCheckoutSessionId] = useState<string | null>(null)
  const [checkoutPublishableKey, setCheckoutPublishableKey] = useState<string | null>(null)
  const [billing, setBilling] = useState<BillingStatus | null>(null)
  const [planPrices, setPlanPrices] = useState<PlanPrices>({ single: 2000, pro2: 3000, pro5: 7200 })
  const reportPacks = availableReportPacks(planPrices)
  const selectedReportPack = reportPacks.find(pack => pack.id === reportPack) ?? reportPacks[0]
  const [billingPortalLoading, setBillingPortalLoading] = useState(false)
  const pendingCheckoutAnalyticsRef = React.useRef<{
    plan: 'single' | 'pack2' | 'pack3' | 'pro2' | 'pro5'
    priceCents: number
    billingCycle: 'monthly' | 'one_time'
    quantity?: number
    previousPlan: 'free' | 'pro2' | 'pro5'
    researchBeforeUpgrade: number
    daysAsFreeUser: number
  } | null>(null)

  const normalizedPlanStatus = (billing?.plan_status || '').toLowerCase()
  const hasBillingIssue = Boolean(!isViewingOtherUser && billing && hasBillingIssueStatus(billing))
  const billingIssueCopy: Record<string, string> = {
    past_due: 'Your latest payment failed. Update your payment method to keep Insight Atlas AI running.',
    incomplete: 'Finish adding a payment method to activate your plan benefits.',
    incomplete_expired: 'Your checkout session expired. Add a payment method to activate your plan.',
    unpaid: 'Your subscription is unpaid. Add a valid payment method to restore access.',
  }
  const billingIssueMessage = billingIssueCopy[normalizedPlanStatus] || 'Update your payment method to avoid interruptions.'
  const shouldShowBillingBanner = hasBillingIssue && billingLoaded
  const billingBadgeLabel = normalizedPlanStatus === 'past_due' ? 'Past due' : 'Action needed'
  const reservedFreeReports = billing?.reserved_free_report_count ?? 0
  const reservedMonthlyReports = billing?.reserved_monthly_report_count ?? 0
  const reservedSingleCredits = billing?.reserved_single_report_count ?? 0
  const availableFreeReports =
    billing?.available_free_report_count ?? billing?.free_reports_remaining ?? 0
  const availableMonthlyReports =
    billing?.available_monthly_report_count ??
    Math.max(0, (billing?.monthly_report_limit ?? 0) - (billing?.monthly_report_used ?? 0) - reservedMonthlyReports)
  const availableSingleCredits =
    billing?.available_single_report_credits ??
    billing?.single_report_credits ??
    0
  const effectiveMonthlyUsed = billing
    ? Math.min(billing.monthly_report_limit, billing.monthly_report_used + reservedMonthlyReports)
    : 0
  const freeReportStatusLabel =
    billing?.lifetime_free_used
      ? 'Used'
      : reservedFreeReports > 0
        ? 'Reserved'
        : 'Available'
  const singleCreditStatusLabel =
    reservedSingleCredits > 0
      ? `${reservedSingleCredits} reserved`
      : availableSingleCredits === 1
        ? 'Credit'
        : 'Credits'
  const billingBadgeIndicator = viewMode === 'mobile'
    ? (
      <span className="relative flex h-2.5 w-2.5 ml-1" aria-label="Billing issue requires attention">
        <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 animate-ping"></span>
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-600"></span>
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-600 px-2 py-0.5 text-xs font-semibold text-white dark:bg-red-500">
        <span className="h-1.5 w-1.5 rounded-full bg-white dark:bg-red-100" aria-hidden="true"></span>
        {billingBadgeLabel}
      </span>
    )

  const planStatusClassMap: Record<string, string> = {
    active: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
    past_due: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200',
    unpaid: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200',
    incomplete: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200',
    incomplete_expired: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200',
    canceled: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  }
  const planStatusLabelMap: Record<string, string> = {
    active: 'Active',
    past_due: 'Past due',
    unpaid: 'Unpaid',
    incomplete: 'Pending activation',
    incomplete_expired: 'Action required',
    canceled: 'Canceled',
  }
  const planStatusBadgeClass = planStatusClassMap[normalizedPlanStatus] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200'
  const planStatusLabel = planStatusLabelMap[normalizedPlanStatus] || 'Status'

  useEffect(() => {
    if (user && !isViewingOtherUser && !isLogoutInProgress()) {
      (async () => {
        try {
          const s = await getBillingStatus()
          if (isLogoutInProgress()) {
            return
          }
          setBilling(s as any)
          setBillingLoaded(true)
          setBillingError(null)
        } catch (e: any) {
          setBillingLoaded(true)
          setBillingError(e?.message || 'Failed to load billing status')
        }
      })()
    }
  }, [isViewingOtherUser, user?.id])

  useEffect(() => {
    getPrices().then(setPlanPrices).catch(() => {})
  }, [])

  // Auto-refresh billing status while Billing tab is active (reflect recent completions)
  useEffect(() => {
    if (!user || activeSection !== 'billing' || isViewingOtherUser || isLogoutInProgress()) return
    let cancelled = false
    const refresh = async () => {
      if (cancelled || isLogoutInProgress()) {
        return
      }
      try {
        const s = await getBillingStatus()
        if (!cancelled) {
          setBilling(s as any)
          setBillingError(null)
          setBillingLoaded(true)
        }
      } catch (e: any) {
        if (!cancelled) {
          setBillingLoaded(true)
          setBillingError(e?.message || 'Failed to load billing status')
        }
      }
    }
    // initial slight delay to allow completion write
    const t = setTimeout(refresh, 1500)
    const interval = setInterval(refresh, 10000)
    return () => { cancelled = true; clearTimeout(t); clearInterval(interval) }
  }, [activeSection, isViewingOtherUser, user?.id])

  const scrollToBillingSection = () => {
    requestAnimationFrame(() => {
      document.getElementById('billing-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }

  const handlePurchase = async (planId: 'single' | 'pro2' | 'pro5') => {
    setPurchaseError(null)
    setPurchaseLoading(planId)
    const purchasePlan = planId === 'single' ? selectedReportPack?.id ?? 'single' : planId
    const reportQuantity = planId === 'single' ? selectedReportPack?.reports ?? 1 : undefined
    const priceCents = planId === 'single' ? (selectedReportPack?.priceCents ?? planPrices.single ?? 2000) : planId === 'pro2' ? (planPrices.pro2 ?? 3000) : (planPrices.pro5 ?? 7200)
    const billingCycle = planId === 'single' ? 'one_time' : 'monthly'
    const previousPlan = (billing?.plan || 'free') as 'free' | 'pro2' | 'pro5'
    const createdAtMs = user?.created_at ? new Date(user.created_at).getTime() : Number.NaN
    const daysAsFreeUser =
      previousPlan === 'free' && Number.isFinite(createdAtMs)
        ? Math.max(0, Math.floor((Date.now() - createdAtMs) / (1000 * 60 * 60 * 24)))
        : 0

    pendingCheckoutAnalyticsRef.current = {
      plan: purchasePlan,
      priceCents,
      billingCycle,
      quantity: reportQuantity,
      previousPlan,
      researchBeforeUpgrade: totalResearch,
      daysAsFreeUser,
    }
    analytics.trackUpgradeStarted({
      target_plan: purchasePlan,
      current_plan: previousPlan,
      trigger: 'profile',
      price_cents: priceCents,
      billing_cycle: billingCycle,
      ...(reportQuantity ? { quantity: reportQuantity } : {}),
    })
    try {
      await createStripeCustomer()
      const result = planId === 'single'
        ? await buySingleReport(1, selectedReportPack?.id ?? 'single')
        : await subscribe(planId)
      if (result.clientSecret) {
        setCheckoutSecret(result.clientSecret)
        setCheckoutSessionId(result.sessionId)
        setCheckoutPublishableKey(result.publishableKey ?? null)
      } else {
        pendingCheckoutAnalyticsRef.current = null
        setPurchaseError('Unable to create payment session. Please contact support.')
      }
    } catch (e: any) {
      pendingCheckoutAnalyticsRef.current = null
      setPurchaseError(e?.message || 'Purchase failed. Please try again.')
    } finally {
      setPurchaseLoading(null)
    }
  }

  const openBillingPortal = async (options?: { requirePaymentMethod?: boolean }) => {
    if (billingPortalLoading) {
      return
    }
    try {
      setBillingPortalLoading(true)
      // Ensure a Stripe customer exists (idempotent)
      try {
        await createStripeCustomer()
      } catch (e) {
        // non-fatal; proceed to portal attempt
      }
      const url = await getBillingPortal({
        returnUrl: window.location.href,
        flowType: options?.requirePaymentMethod ? 'payment_method_update' : 'default'
      })
      window.open(url, '_blank')
      const onFocus = async () => {
        window.removeEventListener('focus', onFocus)
        // Poll a few times so the live Stripe sync has time to detect changes
        for (let i = 0; i < 3; i++) {
          if (isLogoutInProgress()) {
            break
          }
          await new Promise(r => setTimeout(r, i === 0 ? 500 : 2000))
          try {
            const s = await getBillingStatus()
            setBilling(s as any)
            onBillingStatusSync?.(s as any)
            if (s.plan_status === 'canceled' || s.plan === 'free') break
          } catch {}
        }
      }
      window.addEventListener('focus', onFocus)
    } catch (e) {
      onShowToast({
        type: 'error',
        title: 'Billing portal unavailable',
        description: 'Please try again or contact support.'
      })
    } finally {
      setBillingPortalLoading(false)
    }
  }

  return (
    <div className={`min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white ${viewMode === 'mobile' ? '' : 'px-8 py-12'}`}>
      {/* Mobile Header */}
      {viewMode === 'mobile' && (
        <>
          {isViewingOtherUser && onBack ? (
            <div className="bg-white dark:bg-gray-900 sticky top-0 z-20 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-4 px-4 py-4">
                <button
                  onClick={onBack}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {displayName}
                </h1>
              </div>
            </div>
          ) : (
            <PageHeader title="Profile" />
          )}
        </>
      )}

      {/* Main Content */}
      <div id="profile-timeline-scroll" className={viewMode === 'mobile' ? 'px-4 py-4' : 'mx-auto max-w-4xl'} style={{ overflowAnchor: 'none' as any }}>
        {shouldShowBillingBanner && (
          <div className="mb-8 rounded-2xl border border-red-200 bg-red-50 px-4 py-4 text-red-900 shadow-sm dark:border-red-600/40 dark:bg-red-900/30 dark:text-red-50">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 text-red-600 dark:bg-red-800/60 dark:text-red-200">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01M10.29 3.86l-8 13.86A1 1 0 003 19h18a1 1 0 00.85-1.5l-8-13.86a1 1 0 00-1.7 0z" />
                  </svg>
                </span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-red-700 dark:text-red-200">Payment issue detected</p>
                  <p className="text-sm text-red-800 dark:text-red-100">{billingIssueMessage}</p>
                </div>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <button
                  onClick={() => {
                    setActiveSection('billing')
                    scrollToBillingSection()
                  }}
                  className="w-full rounded-lg border border-red-300 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-100/80 dark:border-red-500/60 dark:text-red-100 dark:hover:bg-red-800/40 sm:w-auto"
                >
                  Review billing
                </button>
                <button
                  onClick={() => openBillingPortal({ requirePaymentMethod: true })}
                  disabled={billingPortalLoading}
                  className="w-full rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-red-500 dark:hover:bg-red-400 sm:w-auto"
                >
                  {billingPortalLoading ? 'Opening portal…' : 'Fix now'}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Profile Header Section - Uber-inspired design */}
        <div className="bg-black rounded-xl shadow-lg overflow-hidden mb-8">
          {/* Adaptive Hero Image with Avatar Color */}
          <div className="h-48 relative overflow-hidden bg-black">
            {/* Dynamic gradient mesh background */}
            <div className="absolute inset-0">
              {/* Base gradient - lighter for better color visibility */}
              <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" />
              
              {/* Dynamic color orbs based on avatar - increased opacity for vibrancy */}
              <div 
                className="absolute top-0 left-0 w-96 h-96 rounded-full filter blur-3xl transform -translate-x-1/2 -translate-y-1/2 transition-all duration-1000"
                style={{ backgroundColor: hexToRgba(avatarPrimaryColor, 0.5) }}
              />
              <div 
                className="absolute top-0 right-0 w-96 h-96 rounded-full filter blur-3xl transform translate-x-1/2 -translate-y-1/2 transition-all duration-1000"
                style={{ backgroundColor: hexToRgba(avatarPrimaryColor, 0.4) }}
              />
              <div 
                className="absolute bottom-0 left-1/3 w-96 h-96 rounded-full filter blur-3xl transform translate-y-1/2 transition-all duration-1000"
                style={{ backgroundColor: hexToRgba(avatarPrimaryColor, 0.35) }}
              />
              <div 
                className="absolute bottom-0 right-1/4 w-64 h-64 rounded-full filter blur-3xl transform translate-y-1/2 transition-all duration-1000"
                style={{ backgroundColor: hexToRgba(avatarPrimaryColor, 0.3) }}
              />
              
              {/* Additional center glow for more vibrancy */}
              <div 
                className="absolute top-1/2 left-1/2 w-96 h-96 rounded-full filter blur-3xl transform -translate-x-1/2 -translate-y-1/2 transition-all duration-1000"
                style={{ backgroundColor: hexToRgba(avatarPrimaryColor, 0.25) }}
              />
              
              {/* Modern geometric pattern */}
              <svg className="absolute inset-0 w-full h-full opacity-10" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <pattern id="hero-pattern" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
                    <rect x="0" y="0" width="60" height="60" fill="none" stroke="white" strokeWidth="0.5" />
                    <circle cx="30" cy="30" r="20" fill="none" stroke="white" strokeWidth="0.5" />
                    <path d="M0,30 L60,30 M30,0 L30,60" stroke="white" strokeWidth="0.5" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#hero-pattern)" />
              </svg>
              
              {/* Noise texture overlay for depth */}
              <div className="absolute inset-0 opacity-10" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.3'/%3E%3C/svg%3E")`,
              }} />
              
              {/* Bottom gradient fade for better avatar contrast */}
              <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
            </div>
          </div>
          
          {/* Profile Info */}
          <div className="px-6 pb-6">
            <div className="flex flex-col sm:flex-row items-center sm:items-end gap-4 -mt-16">
              {/* Avatar */}
              <div className="relative group flex-shrink-0">
                <div className="w-32 h-32 rounded-full border-4 border-black shadow-2xl overflow-hidden relative">
                  <Avatar
                    src={isViewingOtherUser ? profileUser?.avatar_url : user?.avatar_url}
                    name={displayName}
                    size="xl"
                    className="w-32 h-32"
                  />
                  {isUploadingAvatar && !isViewingOtherUser && (
                    <div
                      className="absolute inset-0 bg-black/40 flex items-center justify-center pointer-events-none"
                      data-testid="avatar-upload-loader"
                    >
                      <LoadingAnimation size={20} />
                    </div>
                  )}
                </div>
                {!isViewingOtherUser && (
                  <label className="absolute inset-0 flex items-center justify-center bg-black/70 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200 cursor-pointer">
                    <input
                      ref={avatarFileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleAvatarUpload}
                    />
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </label>
                )}
              </div>

              {/* User Info */}
              <div className="flex-1" style={{ 
                minWidth: '200px', 
                textAlign: viewMode === 'mobile' ? 'center' : 'left',
                zIndex: 10,
                position: 'relative'
              }}>
                <h1 className="!text-white !opacity-100 !visible !block" style={{ 
                  fontSize: '30px',
                  fontWeight: '500',
                  margin: '0',
                  padding: '0',
                  lineHeight: '1.2',
                  textShadow: '0 1px 2px rgba(0,0,0,0.5)'
                }}>
                  {displayName}
                </h1>
                <p className="!text-gray-400 !opacity-100 !visible !block" style={{ 
                  fontSize: '18px',
                  marginTop: '4px'
                }}>
                  @{username || emailLocalPart || displayName.toLowerCase().replace(/\s+/g, '')}
                </p>
                {bio && (
                  <p className="!text-gray-300" style={{ 
                    fontSize: '14px',
                    marginTop: '8px'
                  }}>
                    {bio}
                  </p>
                )}
              </div>

              {/* Stats */}
              <div className="flex gap-8 text-center flex-shrink-0">
                <div>
                  <div className="text-2xl font-light text-white">{totalResearch}</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider mt-1">Research</div>
                </div>
                {!isViewingOtherUser && (
                  <>
                    <div>
                      <div className="text-2xl font-light text-white">{publicResearch}</div>
                      <div className="text-xs text-gray-500 uppercase tracking-wider mt-1">Public</div>
                    </div>
                    <div>
                      <div className="text-2xl font-light text-white">{privateResearch}</div>
                      <div className="text-xs text-gray-500 uppercase tracking-wider mt-1">Private</div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs for current user - Uber style */}
        {!isViewingOtherUser && (
          <div className="flex gap-4 mb-8">
            <button
              onClick={() => setActiveSection('timeline')}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                activeSection === 'timeline'
                  ? 'bg-black dark:bg-white text-white dark:text-black shadow-sm'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              Timeline
            </button>
            <button
              onClick={() => {
                setActiveSection('settings')
                engagementTracking.trackSettingsOpened()
              }}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                activeSection === 'settings'
                  ? 'bg-black dark:bg-white text-white dark:text-black shadow-sm'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              Settings
            </button>
            <button
              onClick={() => {
                setActiveSection('billing')
                scrollToBillingSection()
              }}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                activeSection === 'billing'
                  ? 'bg-black dark:bg-white text-white dark:text-black shadow-sm'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
              title="View your plan, manage payment methods, and limits"
            >
              <span className="flex items-center gap-2">
                Billing
                {hasBillingIssue && billingBadgeIndicator}
              </span>
            </button>
          </div>
        )}

        {/* Content based on active section */}
        {(isViewingOtherUser || activeSection === 'timeline') && (
          <div>
              {showOwnMobileProfileCompletionCard && (
                <MobileProfileCompletionCard
                  completionPercentage={profileCompletionPercentage}
                  actions={profileCompletionActions}
                />
              )}
              {!showOwnMobileProfileOnboarding && (
                <h2 className="text-2xl font-medium text-black dark:text-white mb-6">Timeline</h2>
              )}
              {isLoadingResearch ? (
                <div className="flex items-center justify-center py-16">
                  <LoadingAnimation size={48} />
                </div>
              ) : userResearch.length > 0 ? (
                <div className="relative">
                  {/* Timeline line */}
                <div className="absolute left-7 top-0 bottom-0 w-0.5 bg-gray-300 dark:bg-gray-700" />
                  
                <div className="space-y-6">
                    {visibleResearch.map((research, index) => {
                      const date = new Date(research.created_at)
                    const monthYear = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                    const dayNumber = date.getDate()
                    const imageUrl = resolveResearchCardImageUrl(research.image_url, API_BASE_URL) || research.image_url
                    
                    // Show month/year header when it changes
                    const showMonthHeader = index === 0 || 
                      new Date(userResearch[index - 1].created_at).getMonth() !== date.getMonth() ||
                      new Date(userResearch[index - 1].created_at).getFullYear() !== date.getFullYear()
                      
                      return (
                      <div key={research.id} id={`profile-card-${research.id}`} data-profile-card="true" ref={index === userResearch.length - 1 ? (el) => { lastItemRef.current = el } : undefined}>
                        {showMonthHeader && (
                          <div className="flex items-center gap-4 mb-6 mt-8 first:mt-0">
                            <div className="bg-black dark:bg-white text-white dark:text-black px-3 py-1 rounded-full text-sm font-medium z-10">
                              {monthYear}
                            </div>
                            <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                          </div>
                        )}
                        
                        <div className="relative flex gap-6">
                          {/* Timeline dot */}
                          <div className="absolute left-4 w-6 h-6 bg-white dark:bg-gray-800 border-4 border-black dark:border-white rounded-full z-10" />
                          
                          {/* Content card - Uber style */}
                          <div 
                            onClick={async () => {
                              // Track card click for engagement
                              engagementTracking.trackCardClicked()
                              analytics.trackProfileResearchClicked(
                                research.id?.toString() || '',
                                profileUser?.id?.toString() || user?.id?.toString() || '',
                                index
                              )
                              
                              // For intelligence reports, pass directly to handler (App.tsx fetches cockpit data)
                              // For regular research, fetch full details first
                              if (research.is_intelligence_report || research.id?.toString().startsWith('ia-')) {
                                // Intelligence reports are handled by App.tsx with cockpit data fetch
                                onResearchClick?.(research)
                              } else if (research.id) {
                                try {
                                  // Always ensure we have latest details (summary, sources, etc.)
                                  const { getResearchDetails } = await import('../lib/api')
                                  const numericId = typeof research.id === 'number' ? research.id : parseInt(String(research.id), 10)
                                  if (isNaN(numericId)) {
                                    onResearchClick?.(research)
                                    return
                                  }
                                  const fullResearch = await getResearchDetails(numericId)
                                  // Fetch the full report text lazily (not included in details for performance)
                                  const { fetchReportText } = await import('../lib/optimizedApi')
                                  const reportText = await fetchReportText(numericId)

                                  const merged = {
                                    ...research,
                                    category: (fullResearch && fullResearch.category) ? fullResearch.category : research.category,
                                    image_url: resolveAssetUrl(fullResearch?.image_url, API_BASE_URL) || research.image_url,
                                    results: {
                                      ...(fullResearch?.results || research.results || {}),
                                      report_text: reportText || (fullResearch?.results?.report_text ?? ''),
                                      content: reportText || (fullResearch?.results?.content ?? '')
                                    },
                                    needs_full_fetch: false
                                  }

                                  onResearchClick?.(merged)
                                } catch (error) {
                                  console.error('Failed to fetch full research:', error)
                                  onResearchClick?.(research)  // Fallback to partial data
                                }
                              } else {
                                onResearchClick?.(research)
                              }
                            }}
                            className="ml-14 flex-1 group cursor-pointer bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 border border-gray-200 dark:border-gray-700 overflow-hidden"
                          >
                            <div className={`${viewMode === 'mobile' ? 'flex flex-col' : 'flex'}`}>
                                                            {/* Image */}
                              {imageUrl && (
                                <div className={`${viewMode === 'mobile' ? 'w-full h-40' : 'w-48 h-32'} flex-shrink-0 overflow-hidden`}>
                                  <img
                                    src={imageUrl}
                                    alt={getResearchTitle(research)}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                    loading="lazy"
                                    decoding="async"
                                  />
                                </div>
                              )}
                                
                                {/* Content */}
                              <div className="flex-1 p-4">
                                <div className="flex items-start justify-between mb-2">
                                    <div className="flex-1">
                                    <h4 className="font-semibold text-gray-900 dark:text-white line-clamp-1 mb-1">
                                        {getResearchTitle(research)}
                                    </h4>
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-gray-500 dark:text-gray-400">
                                      {/* Date and Time inline */}
                                      <span className="inline-flex items-center gap-1 whitespace-nowrap">
                                        <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        <span>{date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                                      </span>
                                      <span className="inline-flex items-center gap-1 whitespace-nowrap">
                                        <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <span>{date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                                      </span>
                                      {/* Badges */}
                                      {!isViewingOtherUser && (
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs whitespace-nowrap ${
                                          research.is_public 
                                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                                            : 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
                                        }`}>
                                          {research.is_public ? 'Public' : 'Private'}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex-shrink-0 ml-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                  </div>
                                  </div>
                                  {research.summary && (
                                  <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                                      {stripMarkdown(research.summary)}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  
                  {/* Load More Button - Only show for own profile, not when viewing others */}
                  {!isViewingOtherUser && (visibleCount < userResearch.length || hasMore) && !isLoadingResearch && (
                    <div ref={loadMoreRef} className="flex justify-center pt-8 pb-4">
                      <button
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          ;(document.activeElement as HTMLElement | null)?.blur()
                          captureAnchor()
                          // Show more immediately for smooth UX
                          setVisibleCount((prev) => prev + PAGE_SIZE)
                          // Prefetch next batch only when needed
                          const needMore = visibleCount + PAGE_SIZE > userResearch.length
                          if (needMore && hasMore && !isLoadingResearch) {
                            loadCurrentUserResearch(false).finally(() => requestAnimationFrame(() => restoreAnchor()))
                          } else {
                            requestAnimationFrame(() => restoreAnchor())
                          }
                        }}
                        className="px-6 py-3 bg-black dark:bg-white text-white dark:text-black rounded-full hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors font-medium shadow-sm hover:shadow-md"
                      >
                        Load More
                      </button>
                    </div>
                  )}
                  
                  {/* Loading indicator */}
                  {isLoadingResearch && (
                    <div className="flex justify-center pt-8 pb-4">
                      <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
                        <LoadingAnimation size={20} />
                        <span>Loading more research...</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : showOwnMobileProfileOnboarding ? (
                <MobileProfileWelcomeState onStartResearch={onNavigateToResearch} />
              ) : (
                <div className="text-center py-16">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                    <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                <p className="text-gray-500 dark:text-gray-400 text-lg">
                  {isViewingOtherUser ? 'No public research available yet' : 'No research completed yet'}
                </p>
                </div>
              )}
              </div>
            )}
            
        {/* Settings Section */}
        {!isViewingOtherUser && activeSection === 'settings' && (
          <div className="space-y-6">
            {/* Settings Header with Back Button */}
            {settingsSubSection !== 'main' && (
              <button
                onClick={() => setSettingsSubSection('main')}
                className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white transition-colors group"
              >
                <svg className="w-5 h-5 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="font-medium">Back to Settings</span>
              </button>
            )}
            
            {/* Main Settings Menu */}
            {settingsSubSection === 'main' && (
              <div className="space-y-3">
                {/* Profile Entry Card */}
                <button
                  onClick={() => setSettingsSubSection('profile')}
                  className="w-full group"
                >
                  <div className="flex items-center gap-4 p-5 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-700/50 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-lg transition-all duration-300">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gray-900 to-gray-700 dark:from-white dark:to-gray-200 flex items-center justify-center shadow-lg">
                      <svg className="w-6 h-6 text-white dark:text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div className="flex-1 text-left">
                      <h3 className="text-base font-semibold text-black dark:text-white">Profile</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Name, username, bio, and location</p>
                    </div>
                    <svg className="w-5 h-5 text-gray-400 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
                
                {/* Notifications Entry Card */}
                <button
                  onClick={() => setSettingsSubSection('notifications')}
                  className="w-full group"
                >
                  <div className="flex items-center gap-4 p-5 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-700/50 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-lg transition-all duration-300">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-blue-400 flex items-center justify-center shadow-lg">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                    </div>
                    <div className="flex-1 text-left">
                      <h3 className="text-base font-semibold text-black dark:text-white">Notifications</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Email alerts and communication preferences</p>
                    </div>
                    <svg className="w-5 h-5 text-gray-400 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
                
                {/* Privacy & Terms Entry Card */}
                <button
                  onClick={() => setSettingsSubSection('privacy')}
                  className="w-full group"
                >
                  <div className="flex items-center gap-4 p-5 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-700/50 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-lg transition-all duration-300">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-600 to-emerald-400 flex items-center justify-center shadow-lg">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <div className="flex-1 text-left">
                      <h3 className="text-base font-semibold text-black dark:text-white">Privacy & Terms</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Privacy policy and terms of service</p>
                    </div>
                    <svg className="w-5 h-5 text-gray-400 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
                
                {/* Help & Support Entry Card */}
                <button
                  onClick={() => setSettingsSubSection('support')}
                  className="w-full group"
                >
                  <div className="flex items-center gap-4 p-5 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-700/50 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-lg transition-all duration-300">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 to-purple-400 flex items-center justify-center shadow-lg">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="flex-1 text-left">
                      <h3 className="text-base font-semibold text-black dark:text-white">Help & Support</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Contact us with questions or feedback</p>
                    </div>
                    <svg className="w-5 h-5 text-gray-400 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
                
                {/* Account Entry Card */}
                <button
                  onClick={() => setSettingsSubSection('account')}
                  className="w-full group"
                >
                  <div className="flex items-center gap-4 p-5 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-700/50 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-lg transition-all duration-300">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gray-600 to-gray-400 flex items-center justify-center shadow-lg">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div className="flex-1 text-left">
                      <h3 className="text-base font-semibold text-black dark:text-white">Account</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Theme, preferences, and sign out</p>
                    </div>
                    <svg className="w-5 h-5 text-gray-400 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
                
                {/* App Version */}
                <div className="pt-4 text-center">
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    Insight Atlas v{APP_VERSION}
                  </p>
                </div>
              </div>
            )}
            
            {/* Profile Sub-Section */}
            {settingsSubSection === 'profile' && (
              <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl shadow-sm border border-gray-200/50 dark:border-gray-700/50">
                <div className="border-b border-gray-200/50 dark:border-gray-700/50 p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-semibold text-black dark:text-white">Profile Information</h3>
                    {!isEditMode ? (
                      <button
                        onClick={() => setIsEditMode(true)}
                        className="px-4 py-2 text-sm font-medium bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-200 rounded-lg transition-colors"
                      >
                        Edit Profile
                      </button>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          onClick={handleCancelEdit}
                          disabled={isSaving}
                          className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveSettings}
                          disabled={isSaving || Boolean(username && usernameError)}
                          className="px-4 py-2 text-sm font-medium bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {isSaving && <LoadingAnimation size={16} />}
                          {isSaving ? 'Saving...' : 'Save Changes'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="p-8">
                {!isEditMode ? (
                  /* Display Mode - Enhanced Modern Design */
                  <div className="space-y-8">
                    {/* Primary Info Group */}
                    <div className="space-y-6">
                      {/* Name with visual emphasis */}
                      <div className="group">
                        <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">Full Name</p>
                        <div className="flex items-center gap-3">
                          <div className="h-1 w-1 rounded-full bg-black dark:bg-white opacity-40"></div>
                          <p className="text-xl font-semibold text-black dark:text-white">
                            {firstName || lastName ? `${firstName} ${lastName}`.trim() : 'Not set'}
                          </p>
                        </div>
                      </div>

                      {/* Username with @ symbol styling */}
                      {username && (
                        <div className="group">
                          <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">Username</p>
                          <div className="flex items-center gap-3">
                            <div className="h-1 w-1 rounded-full bg-black dark:bg-white opacity-40"></div>
                            <p className="text-xl font-medium">
                              <span className="text-gray-400 dark:text-gray-500">@</span>
                              <span className="text-black dark:text-white">{username}</span>
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Bio with better typography */}
                      {bio && (
                        <div className="group">
                          <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">Bio</p>
                          <div className="flex items-start gap-3">
                            <div className="h-1 w-1 rounded-full bg-black dark:bg-white opacity-40 mt-2"></div>
                            <p className="text-base leading-relaxed text-gray-700 dark:text-gray-300 italic">
                              "{bio}"
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Divider */}
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-200/50 dark:border-gray-700/50"></div>
                      </div>
                      <div className="relative flex justify-center">
                        <span className="bg-white dark:bg-gray-900 px-3">
                          <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </span>
                      </div>
                    </div>

                    {/* Secondary Info Group */}
                    <div className="space-y-6">
                      {/* Location with icon */}
                      <div className="group">
                        <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">Location</p>
                        <div className="flex items-center gap-3">
                          <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <p className="text-lg font-medium text-black dark:text-white">
                            {location || <span className="text-gray-400 dark:text-gray-500 italic">Not specified</span>}
                          </p>
                        </div>
                      </div>

                      {/* Email with icon */}
                      <div className="group">
                        <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">Email Address</p>
                        <div className="flex items-center gap-3">
                          <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <p className="text-lg font-medium text-black dark:text-white">
                            {user?.email}
                          </p>
                        </div>
                      </div>
                    </div>


                  </div>
                ) : (
                  /* Edit Mode - Clean Form Style */
                  <div className="space-y-6">
                    {/* Name Fields */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                          First Name
                        </label>
                        <input
                          type="text"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          placeholder="Enter your first name"
                          className="w-full px-4 py-3 rounded-lg border bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-blue-400/20 focus:outline-none transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                          Last Name
                        </label>
                        <input
                          type="text"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          placeholder="Enter your last name"
                          className="w-full px-4 py-3 rounded-lg border bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-blue-400/20 focus:outline-none transition-colors"
                        />
                      </div>
                    </div>

                    {/* Email Field - Read Only */}
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                        Email
                      </label>
                      <div className="relative">
                        <input
                          type="email"
                          value={user?.email || ''}
                          readOnly
                          className="w-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 cursor-not-allowed"
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Username Field */}
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                        Username
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={username}
                          onChange={async (e) => {
                            const rawValue = e.target.value
                            const value = rawValue.toLowerCase().replace(/[^a-z0-9_]/g, '')
                            setUsername(value)
                            setUsernameError(null)
                            setUsernameAvailable(null)
                            
                            // If empty, no validation needed
                            if (!value) {
                              return
                            }
                            
                            // Validate username format
                            if (!value.match(/^[a-z][a-z0-9_]{2,19}$/)) {
                              if (value.length < 3) {
                                setUsernameError('Username must be at least 3 characters')
                              } else if (value.length > 20) {
                                setUsernameError('Username must be 20 characters or less')
                              } else if (!value.match(/^[a-z]/)) {
                                setUsernameError('Username must start with a letter')
                              } else {
                                setUsernameError('Username can only contain lowercase letters, numbers, and underscores')
                              }
                              setUsernameAvailable(false)
                              return
                            }
                            
                            // Check availability if valid
                            setCheckingUsername(true)
                            try {
                              const { checkUsernameAvailability } = await import('../lib/api')
                              const result = await checkUsernameAvailability(value)
                              setUsernameAvailable(result.available)
                              if (!result.available) {
                                setUsernameError('Username is already taken')
                              }
                            } catch (error) {
                              console.error('Failed to check username:', error)
                              setUsernameError('Failed to check username availability')
                              setUsernameAvailable(null)
                            } finally {
                              setCheckingUsername(false)
                            }
                          }}
                          placeholder="Choose a unique username"
                          className="w-full px-4 py-3 pr-10 rounded-lg border bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-blue-400/20 focus:outline-none transition-colors"
                        />
                        {checkingUsername && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <svg className="animate-spin h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          </div>
                        )}
                        {!checkingUsername && usernameAvailable === true && username && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <svg className="h-5 w-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                        {!checkingUsername && username && usernameAvailable === false && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </div>
                        )}
                      </div>
                      {username && usernameError && (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{usernameError}</p>
                      )}
                    </div>

                    {/* Bio Field */}
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                        Bio
                      </label>
                      <textarea
                        value={bio}
                        onChange={(e) => {
                          if (e.target.value.length <= 150) {
                            setBio(e.target.value)
                          }
                        }}
                        placeholder="Tell us about yourself..."
                        rows={3}
                        className="w-full px-4 py-3 rounded-lg border bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-blue-400/20 focus:outline-none transition-colors resize-none"
                      />
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {bio.length}/150 characters
                      </p>
                    </div>

                    {/* Location Field */}
                    <div className="relative location-dropdown-container">
                      <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                        Location
                      </label>
                      <input
                        type="text"
                        value={locationSearch}
                        onChange={(e) => {
                          setLocationSearch(e.target.value)
                          setShowLocationDropdown(true)
                        }}
                        onFocus={() => setShowLocationDropdown(true)}
                        placeholder="Search for a city..."
                        className="w-full px-4 py-3 rounded-lg border bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-blue-400/20 focus:outline-none transition-colors"
                      />
                      {location && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          Selected: {location}
                        </p>
                      )}
                      
                      {/* Location Dropdown */}
                      {showLocationDropdown && locationSearch && (
                        <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 max-h-60 overflow-y-auto">
                          {LOCATIONS.filter(loc => 
                            loc.name.toLowerCase().includes(locationSearch.toLowerCase())
                          ).map((loc) => (
                            <button
                              key={loc.id}
                              onClick={() => {
                                setLocation(loc.name)
                                setLocationSearch(loc.name)
                                setShowLocationDropdown(false)
                              }}
                              className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            >
                              <span className="mr-2">{loc.flag}</span>
                              {loc.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            )}
            
            {/* Notifications Sub-Section */}
            {settingsSubSection === 'notifications' && (
              <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl shadow-sm border border-gray-200/50 dark:border-gray-700/50">
                <div className="border-b border-gray-200/50 dark:border-gray-700/50 p-6">
                  <h3 className="text-xl font-semibold text-black dark:text-white">Notification Preferences</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Choose what emails you want to receive</p>
                </div>
                <div className="p-8">
                  {notificationPrefsLoading ? (
                    <div className="flex items-center justify-center gap-3 text-gray-500 dark:text-gray-400 py-8">
                      <LoadingAnimation size={24} />
                      <span>Loading preferences...</span>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Research Complete */}
                      <button
                        onClick={() => handleNotificationPrefChange('research_complete', !notificationPrefs.research_complete)}
                        disabled={notificationPrefsSaving}
                        className="w-full group disabled:opacity-60"
                      >
                        <div className="flex items-center justify-between p-5 rounded-xl bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                              <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <div className="text-left">
                              <p className="font-semibold text-gray-900 dark:text-white">Research Complete</p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">Get notified when your research finishes</p>
                            </div>
                          </div>
                          <div className={`relative inline-flex h-7 w-12 items-center rounded-full transition-all ${
                            notificationPrefs.research_complete ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                          }`}>
                            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-all ${
                              notificationPrefs.research_complete ? 'translate-x-6' : 'translate-x-1'
                            }`} />
                          </div>
                        </div>
                      </button>
                      
                      {/* Research Issues */}
                      <button
                        onClick={() => handleNotificationPrefChange('research_failed', !notificationPrefs.research_failed)}
                        disabled={notificationPrefsSaving}
                        className="w-full group disabled:opacity-60"
                      >
                        <div className="flex items-center justify-between p-5 rounded-xl bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                              <svg className="w-6 h-6 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                            </div>
                            <div className="text-left">
                              <p className="font-semibold text-gray-900 dark:text-white">Research Issues</p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">Get alerts if something goes wrong</p>
                            </div>
                          </div>
                          <div className={`relative inline-flex h-7 w-12 items-center rounded-full transition-all ${
                            notificationPrefs.research_failed ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                          }`}>
                            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-all ${
                              notificationPrefs.research_failed ? 'translate-x-6' : 'translate-x-1'
                            }`} />
                          </div>
                        </div>
                      </button>
                      
                      {/* Product Updates */}
                      <button
                        onClick={() => handleNotificationPrefChange('marketing', !notificationPrefs.marketing)}
                        disabled={notificationPrefsSaving}
                        className="w-full group disabled:opacity-60"
                      >
                        <div className="flex items-center justify-between p-5 rounded-xl bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                              <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                              </svg>
                            </div>
                            <div className="text-left">
                              <p className="font-semibold text-gray-900 dark:text-white">Product Updates</p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">News about features and improvements</p>
                            </div>
                          </div>
                          <div className={`relative inline-flex h-7 w-12 items-center rounded-full transition-all ${
                            notificationPrefs.marketing ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                          }`}>
                            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-all ${
                              notificationPrefs.marketing ? 'translate-x-6' : 'translate-x-1'
                            }`} />
                          </div>
                        </div>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Privacy Sub-Section */}
            {settingsSubSection === 'privacy' && (
              <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl shadow-sm border border-gray-200/50 dark:border-gray-700/50">
                <div className="border-b border-gray-200/50 dark:border-gray-700/50 p-6">
                  <h3 className="text-xl font-semibold text-black dark:text-white">Privacy & Terms</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Our policies and your rights</p>
                </div>
                <div className="p-8 max-h-[70vh] overflow-y-auto">
                  <div className="space-y-8 max-h-[600px] overflow-y-auto pr-4">
                    {/* Privacy Policy */}
                    <div>
                      <h4 className="text-lg font-semibold text-black dark:text-white mb-4">Privacy Policy</h4>
                      <div className="prose prose-sm dark:prose-invert max-w-none">
                        <p className="text-gray-600 dark:text-gray-400 mb-3">
                          <strong>Effective Date:</strong> November 2024
                        </p>
                        <p className="text-gray-600 dark:text-gray-400 mb-4">
                          Insight Atlas AI ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered research platform.
                        </p>
                        
                        <h5 className="text-sm font-semibold text-black dark:text-white mt-4 mb-2">1. Information We Collect</h5>
                        <ul className="list-disc pl-5 text-gray-600 dark:text-gray-400 space-y-1 text-sm">
                          <li><strong>Account Information:</strong> Email address, name, and profile picture when you sign up via Google OAuth</li>
                          <li><strong>Research Data:</strong> Queries you submit, research reports generated, and associated metadata</li>
                          <li><strong>Usage Data:</strong> Log data, device information, IP addresses, and how you interact with our service</li>
                          <li><strong>Payment Information:</strong> Processed securely by Stripe; we do not store full payment card details</li>
                        </ul>
                        
                        <h5 className="text-sm font-semibold text-black dark:text-white mt-4 mb-2">2. How We Use Your Information</h5>
                        <ul className="list-disc pl-5 text-gray-600 dark:text-gray-400 space-y-1 text-sm">
                          <li>To provide, maintain, and improve our research services</li>
                          <li>To process your research requests using AI and third-party APIs</li>
                          <li>To communicate with you about your account and our services</li>
                          <li>To ensure the security and integrity of our platform</li>
                          <li>To comply with legal obligations</li>
                        </ul>
                        
                        <h5 className="text-sm font-semibold text-black dark:text-white mt-4 mb-2">3. Data Sharing & Disclosure</h5>
                        <ul className="list-disc pl-5 text-gray-600 dark:text-gray-400 space-y-1 text-sm">
                          <li><strong>AI Service Providers:</strong> We use OpenAI to process research queries. Your queries are sent to these services to generate responses.</li>
                          <li><strong>Payment Processors:</strong> Stripe processes all payment transactions</li>
                          <li><strong>Legal Requirements:</strong> We may disclose information if required by law or to protect our rights</li>
                          <li>We do not sell your personal information to third parties</li>
                        </ul>
                        
                        <h5 className="text-sm font-semibold text-black dark:text-white mt-4 mb-2">4. Data Security</h5>
                        <p className="text-gray-600 dark:text-gray-400 text-sm">
                          We implement industry-standard security measures including encryption in transit (TLS/SSL), secure authentication, and regular security audits. However, no method of transmission over the Internet is 100% secure.
                        </p>
                        
                        <h5 className="text-sm font-semibold text-black dark:text-white mt-4 mb-2">5. Your Rights</h5>
                        <ul className="list-disc pl-5 text-gray-600 dark:text-gray-400 space-y-1 text-sm">
                          <li>Access and download your data</li>
                          <li>Request deletion of your account and associated data</li>
                          <li>Opt out of marketing communications</li>
                          <li>Control your notification preferences</li>
                        </ul>
                        
                        <h5 className="text-sm font-semibold text-black dark:text-white mt-4 mb-2">6. Data Retention</h5>
                        <p className="text-gray-600 dark:text-gray-400 text-sm">
                          We retain your data for as long as your account is active or as needed to provide services. You can request deletion at any time by contacting support.
                        </p>
                        
                        <h5 className="text-sm font-semibold text-black dark:text-white mt-4 mb-2">7. Contact Us</h5>
                        <p className="text-gray-600 dark:text-gray-400 text-sm">
                          For privacy-related inquiries, contact us at: <a href="mailto:admin@insightatlas.ai" className="text-black dark:text-white underline">admin@insightatlas.ai</a>
                        </p>
                      </div>
                    </div>
                    
                    {/* Terms of Service */}
                    <div className="border-t border-gray-200/50 dark:border-gray-700/50 pt-8">
                      <h4 className="text-lg font-semibold text-black dark:text-white mb-4">Terms of Service</h4>
                      <div className="prose prose-sm dark:prose-invert max-w-none">
                        <p className="text-gray-600 dark:text-gray-400 mb-3">
                          <strong>Effective Date:</strong> November 2024
                        </p>
                        <p className="text-gray-600 dark:text-gray-400 mb-4">
                          By accessing or using Insight Atlas AI, you agree to be bound by these Terms of Service.
                        </p>
                        
                        <h5 className="text-sm font-semibold text-black dark:text-white mt-4 mb-2">1. Service Description</h5>
                        <p className="text-gray-600 dark:text-gray-400 text-sm">
                          Insight Atlas AI provides AI-powered research and analysis services. Our platform generates research reports based on user queries using artificial intelligence and publicly available information.
                        </p>
                        
                        <h5 className="text-sm font-semibold text-black dark:text-white mt-4 mb-2">2. Account Terms</h5>
                        <ul className="list-disc pl-5 text-gray-600 dark:text-gray-400 space-y-1 text-sm">
                          <li>You must be at least 18 years old to use this service</li>
                          <li>You are responsible for maintaining the security of your account</li>
                          <li>You may not use the service for any illegal or unauthorized purpose</li>
                          <li>One person or entity may not maintain multiple free accounts</li>
                        </ul>
                        
                        <h5 className="text-sm font-semibold text-black dark:text-white mt-4 mb-2">3. Acceptable Use</h5>
                        <p className="text-gray-600 dark:text-gray-400 text-sm mb-2">You agree NOT to use the service to:</p>
                        <ul className="list-disc pl-5 text-gray-600 dark:text-gray-400 space-y-1 text-sm">
                          <li>Generate illegal, harmful, or misleading content</li>
                          <li>Violate any applicable laws or regulations</li>
                          <li>Infringe on intellectual property rights</li>
                          <li>Harass, abuse, or harm others</li>
                          <li>Attempt to gain unauthorized access to our systems</li>
                          <li>Resell or redistribute our services without authorization</li>
                        </ul>
                        
                        <h5 className="text-sm font-semibold text-black dark:text-white mt-4 mb-2">4. AI-Generated Content Disclaimer</h5>
                        <p className="text-gray-600 dark:text-gray-400 text-sm">
                          Research reports are generated by artificial intelligence and may contain inaccuracies. Users should verify important information from primary sources. We do not guarantee the accuracy, completeness, or reliability of AI-generated content.
                        </p>
                        
                        <h5 className="text-sm font-semibold text-black dark:text-white mt-4 mb-2">5. Billing & Refunds</h5>
                        <ul className="list-disc pl-5 text-gray-600 dark:text-gray-400 space-y-1 text-sm">
                          <li>Subscription fees are billed in advance on a monthly basis</li>
                          <li>Refunds may be issued at our discretion within 7 days of charge</li>
                          <li>You may cancel your subscription at any time</li>
                          <li>We reserve the right to modify pricing with 30 days notice</li>
                        </ul>
                        
                        <h5 className="text-sm font-semibold text-black dark:text-white mt-4 mb-2">6. Intellectual Property</h5>
                        <p className="text-gray-600 dark:text-gray-400 text-sm">
                          You retain ownership of your queries and research outputs. We retain all rights to our platform, technology, and branding. By using our service, you grant us a license to process your queries for service delivery.
                        </p>
                        
                        <h5 className="text-sm font-semibold text-black dark:text-white mt-4 mb-2">7. Limitation of Liability</h5>
                        <p className="text-gray-600 dark:text-gray-400 text-sm">
                          TO THE MAXIMUM EXTENT PERMITTED BY LAW, INSIGHT ATLAS AI SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE.
                        </p>
                        
                        <h5 className="text-sm font-semibold text-black dark:text-white mt-4 mb-2">8. Termination</h5>
                        <p className="text-gray-600 dark:text-gray-400 text-sm">
                          We may terminate or suspend your access immediately, without prior notice, for any breach of these Terms. Upon termination, your right to use the service will immediately cease.
                        </p>
                        
                        <h5 className="text-sm font-semibold text-black dark:text-white mt-4 mb-2">9. Changes to Terms</h5>
                        <p className="text-gray-600 dark:text-gray-400 text-sm">
                          We reserve the right to modify these terms at any time. Continued use after changes constitutes acceptance of the new terms.
                        </p>
                        
                        <h5 className="text-sm font-semibold text-black dark:text-white mt-4 mb-2">10. Governing Law</h5>
                        <p className="text-gray-600 dark:text-gray-400 text-sm">
                          These Terms shall be governed by the laws of the United States without regard to conflict of law provisions.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Support Sub-Section */}
            {settingsSubSection === 'support' && (
              <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl shadow-sm border border-gray-200/50 dark:border-gray-700/50">
                <div className="border-b border-gray-200/50 dark:border-gray-700/50 p-6">
                  <h3 className="text-xl font-semibold text-black dark:text-white">Help & Support</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">We're here to help</p>
                </div>
                <div className="p-8">
                  {supportSubmitted ? (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                        <svg className="w-10 h-10 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <h4 className="text-xl font-semibold text-black dark:text-white mb-3">Message Sent!</h4>
                      <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                        Thank you for reaching out. We'll respond to <strong className="text-black dark:text-white">{user?.email}</strong> as soon as possible.
                      </p>
                      <button
                        onClick={() => setSupportSubmitted(false)}
                        className="px-6 py-3 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors font-medium"
                      >
                        Send Another Message
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleSupportSubmit} className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                          Subject
                        </label>
                        <input
                          type="text"
                          value={supportSubject}
                          onChange={(e) => setSupportSubject(e.target.value)}
                          placeholder="What can we help you with?"
                          maxLength={200}
                          className="w-full px-4 py-4 rounded-xl border bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white border-gray-200 dark:border-gray-700 focus:border-black dark:focus:border-white focus:ring-0 focus:outline-none transition-colors"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                          Message <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          value={supportMessage}
                          onChange={(e) => setSupportMessage(e.target.value)}
                          placeholder="Describe your question or issue in detail..."
                          rows={6}
                          maxLength={5000}
                          required
                          className="w-full px-4 py-4 rounded-xl border bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white border-gray-200 dark:border-gray-700 focus:border-black dark:focus:border-white focus:ring-0 focus:outline-none transition-colors resize-none"
                        />
                        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                          {supportMessage.length}/5000 characters
                        </p>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          We'll reply to <strong className="text-black dark:text-white">{user?.email}</strong>
                        </p>
                        <button
                          type="submit"
                          disabled={supportSubmitting || !supportMessage.trim()}
                          className="w-full sm:w-auto px-8 py-4 bg-black dark:bg-white text-white dark:text-black rounded-xl hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          {supportSubmitting ? (
                            <>
                              <LoadingAnimation size={18} />
                              Sending...
                            </>
                          ) : (
                            <>
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                              </svg>
                              Send Message
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            )}
            
            {/* Account Sub-Section */}
            {settingsSubSection === 'account' && (
              <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl shadow-sm border border-gray-200/50 dark:border-gray-700/50">
                <div className="border-b border-gray-200/50 dark:border-gray-700/50 p-6">
                  <h3 className="text-xl font-semibold text-black dark:text-white">Account Settings</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Appearance and session</p>
                </div>
                <div className="p-8 space-y-6">
                  {/* Theme Toggle */}
                  <div>
                    <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-4">Appearance</p>
                    <button
                      onClick={() => onThemeChange(themeMode === 'light' ? 'dark' : 'light')}
                      className="w-full"
                    >
                      <div className="flex items-center justify-between p-5 rounded-xl bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 dark:from-indigo-500 dark:to-purple-600 flex items-center justify-center shadow-lg">
                            {themeMode === 'light' ? (
                              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                              </svg>
                            ) : (
                              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                              </svg>
                            )}
                          </div>
                          <div className="text-left">
                            <p className="font-semibold text-gray-900 dark:text-white">Theme</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {themeMode === 'light' ? 'Light mode' : 'Dark mode'} is active
                            </p>
                          </div>
                        </div>
                        <div className={`relative inline-flex h-7 w-12 items-center rounded-full transition-all ${
                          themeMode === 'dark' ? 'bg-indigo-600' : 'bg-amber-500'
                        }`}>
                          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-all ${
                            themeMode === 'dark' ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </div>
                      </div>
                    </button>
                  </div>
                  
                  {/* Sign Out */}
                  <div>
                    <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-4">Session</p>
                    <button
                      onClick={handleLogout}
                      className="w-full"
                    >
                      <div className="flex items-center justify-between p-5 rounded-xl bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 transition-all border border-red-200/50 dark:border-red-800/30">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                          </div>
                          <div className="text-left">
                            <p className="font-semibold text-red-700 dark:text-red-400">Sign Out</p>
                            <p className="text-sm text-red-600/70 dark:text-red-400/60">End your current session</p>
                          </div>
                        </div>
                        <svg className="w-5 h-5 text-red-400 dark:text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </button>
                  </div>
                  
                  {/* Version Info */}
                  <div className="pt-6 border-t border-gray-200/50 dark:border-gray-700/50">
                    <p className="text-center text-sm text-gray-400 dark:text-gray-500">
                      Insight Atlas v{APP_VERSION}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Billing Section */}
        {!isViewingOtherUser && activeSection === 'billing' && (
          <div id="billing-section" className="grid gap-6">
            {/* Plan Card */}
            <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50">
              <div className="border-b border-gray-200/50 dark:border-gray-700/50 p-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-black dark:text-white">Plan</h3>
                  {billing?.current_period_end && billing.plan !== 'free' ? (
                    <span className="text-xs text-gray-500 dark:text-gray-400 text-right max-w-xs">
                      <span className="block">
                        {billing.plan_status === 'canceled' ? 'Expires' : 'Next renewal'}:{' '}
                        {formatSubscriptionPeriodEndUnix(billing.current_period_end)}
                      </span>
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="p-6 md:p-8">
                {!billingLoaded ? (
                  <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
                    <LoadingAnimation size={20} />
                    <span>Loading billing status…</span>
                  </div>
                ) : billingError ? (
                  <div className="p-4 rounded-lg bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300">
                    {billingError}
                  </div>
                ) : billing ? (
                  <div className="space-y-8">
                    {hasBillingIssue && (
                      <div className="rounded-xl bg-red-50 dark:bg-red-900/20 px-5 py-4 text-sm text-red-700 dark:text-red-200 flex items-center justify-between gap-4">
                        <span>{billingIssueMessage}</span>
                        <button
                          onClick={() => openBillingPortal({ requirePaymentMethod: true })}
                          disabled={billingPortalLoading}
                          className="shrink-0 rounded-lg bg-red-600 px-4 py-2 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                        >
                          {billingPortalLoading ? 'Opening…' : 'Fix now'}
                        </button>
                      </div>
                    )}

                    {/* Report Balance */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Your Report Balance</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {/* Free report */}
                        <div className={`rounded-xl border p-4 ${availableFreeReports > 0 ? 'border-green-300 dark:border-green-700 bg-green-50/50 dark:bg-green-900/10' : 'border-gray-200 dark:border-gray-700'}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <div className={`w-2 h-2 rounded-full ${availableFreeReports > 0 || reservedFreeReports > 0 ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`} />
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Free Report</span>
                          </div>
                          <div className="text-2xl font-bold text-gray-900 dark:text-white">{availableFreeReports}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{freeReportStatusLabel}</div>
                        </div>

                        {/* Monthly subscription reports (if subscribed) */}
                        {billing.plan !== 'free' && (
                          <div className="rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10 p-4">
                            <div className="flex items-center gap-2 mb-1">
                              <div className="w-2 h-2 rounded-full bg-blue-500" />
                              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                {billing.plan === 'pro2' ? 'Pro-2' : 'Pro-5'} Monthly
                              </span>
                            </div>
                            <div className="text-2xl font-bold text-gray-900 dark:text-white">
                              {availableMonthlyReports}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              {reservedMonthlyReports > 0
                                ? `${reservedMonthlyReports} reserved`
                                : `of ${billing.monthly_report_limit} remaining`}
                            </div>
                            <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                              <div
                                className="bg-blue-500 h-1.5 rounded-full transition-all"
                                style={{ width: `${Math.min(100, (effectiveMonthlyUsed / Math.max(1, billing.monthly_report_limit)) * 100)}%` }}
                              />
                            </div>
                          </div>
                        )}

                        {/* Purchased credits */}
                        <div className={`rounded-xl border p-4 ${availableSingleCredits > 0 || reservedSingleCredits > 0 ? 'border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-900/10' : 'border-gray-200 dark:border-gray-700'}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <div className={`w-2 h-2 rounded-full ${availableSingleCredits > 0 || reservedSingleCredits > 0 ? 'bg-purple-500' : 'bg-gray-300 dark:bg-gray-600'}`} />
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Purchased</span>
                          </div>
                          <div className="text-2xl font-bold text-gray-900 dark:text-white">{availableSingleCredits}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{singleCreditStatusLabel}</div>
                        </div>
                      </div>
                    </div>

                    {/* Plan Options - always visible */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                        {billing.plan === 'free' ? 'Get More Reports' : 'Your Plan'}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Single Report */}
                        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 p-5 flex flex-col">
                          <div className="flex items-center justify-between mb-1">
                            <h5 className="font-semibold text-gray-900 dark:text-white">{reportPacks.length > 1 ? 'One-time Reports' : 'Single Report'}</h5>
                          </div>
                          <p className="text-gray-500 dark:text-gray-400 text-sm mb-3">{formatUsd(selectedReportPack?.priceCents ?? planPrices.single ?? 2000)} <span className="text-xs">one-time</span></p>
                          <ul className="space-y-1.5 text-xs text-gray-600 dark:text-gray-400 mb-4 flex-1">
                            <li className="flex items-center gap-2">
                              <svg className="w-3.5 h-3.5 text-green-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                              Full-depth Intelligence Report
                            </li>
                            <li className="flex items-center gap-2">
                              <svg className="w-3.5 h-3.5 text-green-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                              No subscription required
                            </li>
                          </ul>
                          <div className="mb-3">
                            <ReportPackPicker options={reportPacks} value={selectedReportPack?.id ?? 'single'} onChange={setReportPack} />
                          </div>
                          <button
                            onClick={() => handlePurchase('single')}
                            disabled={purchaseLoading !== null}
                            className="w-full py-2.5 rounded-xl text-sm font-semibold bg-gray-900 dark:bg-gray-700 text-white hover:bg-gray-800 dark:hover:bg-gray-600 transition-colors border border-gray-300 dark:border-gray-600 disabled:opacity-50"
                          >
                            {purchaseLoading === 'single' ? 'Processing...' : selectedReportPack && selectedReportPack.reports > 1 ? `Buy ${selectedReportPack.reports} Reports — ${formatUsd(selectedReportPack.priceCents)}` : 'Buy Report'}
                          </button>
                        </div>

                        {/* Pro-2 */}
                        <div className={`rounded-2xl border p-5 flex flex-col ${billing.plan === 'pro2' ? 'border-blue-500 ring-1 ring-blue-500/20' : 'border-gray-200 dark:border-gray-700'}`}>
                          <div className="flex items-center justify-between mb-1">
                            <h5 className="font-semibold text-gray-900 dark:text-white">Pro-2</h5>
                            {billing.plan === 'pro2' && <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 px-2 py-0.5 rounded-full">CURRENT</span>}
                          </div>
                          <p className="text-gray-500 dark:text-gray-400 text-sm mb-3">{planPrices.pro2 ? `$${Math.floor(planPrices.pro2 / 100)}` : '$30'} <span className="text-xs">/month</span></p>
                          <ul className="space-y-1.5 text-xs text-gray-600 dark:text-gray-400 mb-4 flex-1">
                            <li className="flex items-center gap-2">
                              <svg className="w-3.5 h-3.5 text-green-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                              2 Intelligence Reports / month
                            </li>
                            <li className="flex items-center gap-2">
                              <svg className="w-3.5 h-3.5 text-green-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                              Priority processing
                            </li>
                          </ul>
                          {billing.plan === 'pro2' && billing.plan_status !== 'canceled' ? (
                            <button
                              onClick={() => openBillingPortal({})}
                              disabled={billingPortalLoading}
                              className="w-full py-2.5 rounded-xl text-sm font-semibold bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                            >
                              {billingPortalLoading ? 'Opening…' : 'Manage'}
                            </button>
                          ) : (
                            <button
                              onClick={() => handlePurchase('pro2')}
                              disabled={purchaseLoading !== null}
                              className="w-full py-2.5 rounded-xl text-sm font-semibold bg-gray-900 dark:bg-gray-700 text-white hover:bg-gray-800 dark:hover:bg-gray-600 transition-colors border border-gray-300 dark:border-gray-600 disabled:opacity-50"
                            >
                              {purchaseLoading === 'pro2' ? 'Processing...' : 'Subscribe'}
                            </button>
                          )}
                        </div>

                        {/* Pro-5 */}
                        <div className={`rounded-2xl p-5 flex flex-col ${billing.plan === 'pro5' ? 'border-2 border-blue-500 ring-1 ring-blue-500/20 bg-gradient-to-b from-gray-900 to-gray-800 dark:from-gray-800 dark:to-gray-900' : 'border-2 border-blue-500/40 bg-gradient-to-b from-gray-900 to-gray-800 dark:from-gray-800 dark:to-gray-900'}`}>
                          <div className="flex items-center justify-between mb-1">
                            <h5 className="font-semibold text-white">Pro-5</h5>
                            {billing.plan === 'pro5'
                              ? <span className="text-[10px] font-bold text-blue-300 bg-blue-900/50 px-2 py-0.5 rounded-full">CURRENT</span>
                              : <span className="text-[10px] font-bold text-white bg-blue-500 px-2 py-0.5 rounded-full">BEST VALUE</span>
                            }
                          </div>
                          <p className="text-gray-400 text-sm mb-3">{planPrices.pro5 ? `$${Math.floor(planPrices.pro5 / 100)}` : '$72'} <span className="text-xs">/month</span></p>
                          <ul className="space-y-1.5 text-xs text-gray-300 mb-4 flex-1">
                            <li className="flex items-center gap-2">
                              <svg className="w-3.5 h-3.5 text-green-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                              5 Intelligence Reports / month
                            </li>
                            <li className="flex items-center gap-2">
                              <svg className="w-3.5 h-3.5 text-green-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                              Priority processing & support
                            </li>
                          </ul>
                          {billing.plan === 'pro5' && billing.plan_status !== 'canceled' ? (
                            <button
                              onClick={() => openBillingPortal({})}
                              disabled={billingPortalLoading}
                              className="w-full py-2.5 rounded-xl text-sm font-semibold bg-white/10 text-white hover:bg-white/20 transition-colors"
                            >
                              {billingPortalLoading ? 'Opening…' : 'Manage'}
                            </button>
                          ) : (
                            <button
                              onClick={() => handlePurchase('pro5')}
                              disabled={purchaseLoading !== null}
                              className="w-full py-2.5 rounded-xl text-sm font-semibold bg-blue-500 text-white hover:bg-blue-600 transition-colors disabled:opacity-50"
                            >
                              {purchaseLoading === 'pro5' ? 'Processing...' : 'Subscribe'}
                            </button>
                          )}
                        </div>
                      </div>
                      {purchaseError && (
                        <div className="mt-3 p-3 rounded-xl bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300 text-sm">
                          {purchaseError}
                        </div>
                      )}
                      <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-3">
                        Founding / Beta pricing. Cancel subscriptions anytime. Taxes may apply.
                      </p>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <CheckoutModal
              clientSecret={checkoutSecret}
              sessionId={checkoutSessionId}
              publishableKey={checkoutPublishableKey}
              onClose={() => {
                pendingCheckoutAnalyticsRef.current = null
                setCheckoutSecret(null)
                setCheckoutSessionId(null)
                setCheckoutPublishableKey(null)
              }}
              onComplete={async () => {
                const checkoutAnalytics = pendingCheckoutAnalyticsRef.current
                if (checkoutAnalytics) {
                  analytics.trackUpgradeCompleted({
                    plan: checkoutAnalytics.plan,
                    price_cents: checkoutAnalytics.priceCents,
                    billing_cycle: checkoutAnalytics.billingCycle,
                    previous_plan: checkoutAnalytics.previousPlan,
                    days_as_free_user: checkoutAnalytics.daysAsFreeUser,
                    research_before_upgrade: checkoutAnalytics.researchBeforeUpgrade,
                    ...(checkoutAnalytics.quantity ? { quantity: checkoutAnalytics.quantity } : {}),
                  })
                }
                pendingCheckoutAnalyticsRef.current = null
                setCheckoutSecret(null)
                setCheckoutSessionId(null)
                setCheckoutPublishableKey(null)
                onShowToast({ type: 'success', title: 'Payment successful', description: 'Your account has been updated.' })
                try {
                  const s = await getBillingStatus()
                  setBilling(s as any)
                  onBillingStatusSync?.(s as any)
                } catch {}
              }}
            />
          </div>
        )}
      </div>
    </div>
  )
} 